// useGroupOrderPrint.tsx
import { useState } from "react";
import { LOGO_URL } from "../constants/constans";
import { Group } from "../redux/deliveryRouteOptimizer";

// Base interfaces for delivery route optimizer
interface CustomItem {
  displayName: string;
  quantity: number;
  unitPrice: number;
  productId?: string;
  tinyId?: number;
  isAddOn?: boolean;
}

interface BaseOrderItem {
  menuItemId: string;
  displayName: string;
  itemType: string;
  quantity: number;
  unitPrice: number;
  unitTotalPrice?: number;
  portionSize?: string;
  customItems?: CustomItem[];
  perItemManualDiscount?: number;
  tinyId?: number;
}

interface BaseCustomer {
  id: string;
  displayName: string;
  userId?: string;
  phoneNumber?: string;
  email?: string;
}

interface BaseOrganization {
  displayName: string;
  organizationId: string;
  phoneNumber: string;
}

// For Delivery Route Optimizer
export interface PrintDeliveryOrder {
  id: string;
  orderStatus: string;
  status: "unassigned" | "grouped" | "assigned";
  groupId?: any;
  vehicleId?: string | number | null;
  orderReference: string;
  totalPrice: number;
  paymentMethod: string;
  phoneNumber: string;
  displayName: string;
  customer: BaseCustomer;
  organization: BaseOrganization;
  orderItems: BaseOrderItem[];
  deliveryDetails: any;
  paymentSummary: any;
  createdAt: any;
  deliverAt: any;
  isDeliveryOrder: boolean;
  isDirectRestaurantOrder: boolean;
  isScheduledOrder: boolean;
  paymentCompleted: boolean;
  lat?: number;
  lng?: number;
  address?: string;
  groupedForCollection: boolean;
  orderNote?: string;
  packingNote?: string;
  paymentNote?: string;
  cart?: {
    cartId: string;
    displayName: string;
    cartPersonId?: string;
    userId?: string;
    cartPersonName?: string;
  };
}

interface UseGroupOrderPrintProps {
  groups?: Group[];
  deliveries?: PrintDeliveryOrder[];
  vehicles?: any[];
}

export interface PrintByCartOptions {
  cartId: string;
  cartName: string;
  copyType: "rider" | "customer";
}

export interface PrintByGroupOptions {
  groupId: string;
  groupName: string;
  copyType: "rider" | "customer";
}

export interface PrintByOrderOptions {
  orderId: string;
  orderReference: string;
  copyType: "rider" | "customer";
}

export const useGroupOrderPrint = ({
  groups = [],
  deliveries = [],
  vehicles = [],
}: UseGroupOrderPrintProps) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");

  // Format distance for display
  const formatDistance = (distanceInKm: number): string => {
    return `${distanceInKm.toFixed(2)} km`;
  };

  // Type guard to check if value is defined
  const isDefined = <T>(value: T | undefined | null): value is T => {
    return value !== undefined && value !== null;
  };

  // Type guards for customer and organization
  const hasCustomer = (
    order: PrintDeliveryOrder
  ): order is PrintDeliveryOrder & { customer: BaseCustomer } => {
    return order.customer != null && order.customer.displayName != null;
  };

  const hasOrganization = (
    order: PrintDeliveryOrder
  ): order is PrintDeliveryOrder & { organization: BaseOrganization } => {
    return !!(order.organization && order.organization.displayName);
  };

  const getEntityInfo = (order: PrintDeliveryOrder) => {
    if (hasOrganization(order)) {
      return {
        type: "ORGANIZATION" as const,
        id: order.organization.organizationId,
        name: order.organization.displayName,
        label: "Organization Name",
      };
    }

    if (hasCustomer(order)) {
      const customerInfo = order as PrintDeliveryOrder & {
        customer: BaseCustomer;
      };
      return {
        type: "CUSTOMER" as const,
        id: customerInfo.customer.id,
        name: customerInfo.customer.displayName,
        label: "Customer Name",
      };
    }

    return null;
  };

  // Get cart information
  const getCartInfo = (orderId?: string) => {
    // Try to find the specific group this order belongs to
    if (orderId && groups && groups.length > 0) {
      const orderGroup = groups.find((g) => g.deliveryIds?.includes(orderId));
      if (orderGroup?.cart) {
        return {
          cartDisplayName: orderGroup.cart.displayName || "",
          riderName: orderGroup.cart.cartPersonName || "",
        };
      }
    }

    // Fallback: original behavior if no orderId given or no matching group found
    if (groups && groups.length > 0 && groups[0].cart) {
      return {
        cartDisplayName: groups[0].cart.displayName || "",
        riderName: groups[0].cart.cartPersonName || "",
      };
    }

    return {
      cartDisplayName: "",
      riderName: "",
    };
  };

  // Get all available orders for individual printing
  const getAllAvailableOrders = (): PrintDeliveryOrder[] => {
    const allOrders: PrintDeliveryOrder[] = [];

    // Add orders from deliveries
    if (deliveries && deliveries.length > 0) {
      deliveries.forEach((delivery) => {
        allOrders.push(delivery);
      });
    }

    // Remove duplicates by order ID
    const uniqueOrders = allOrders.filter(
      (order, index, self) => index === self.findIndex((o) => o.id === order.id)
    );

    return uniqueOrders;
  };

  // Helper function to create a unique key for each item
  const createItemKey = (item: BaseOrderItem): string => {
    if (
      item.itemType === "CustomMenu" &&
      item.customItems &&
      item.customItems.length > 0
    ) {
      // For CustomMenu with custom items, create a key that includes custom items with their quantities
      const customItemsKey = item.customItems
        .map((customItem) => `${customItem.displayName}_${customItem.quantity}`)
        .sort()
        .join("|");
      return `${item.displayName}_${customItemsKey}`;
    }
    // For regular items, just use displayName
    return item.displayName;
  };

  // Helper function to create a unique key for custom items within CustomMenu
  const createCustomItemKey = (customItem: CustomItem): string => {
    return customItem.displayName;
  };

  // Format display name to include tinyId if available
  const formatDisplayName = (displayName: string, tinyId?: number): string => {
    if (tinyId) {
      return `${displayName} <span class="tiny-id-highlight">(${tinyId})</span>`;
    }
    return displayName;
  };

  // Helper to determine CSS class based on name length for better wrapping
  const getItemDisplayNameClass = (displayName: string): string => {
    return "packing-name-normal";
  };

  const getCustomItemDisplayNameClass = (displayName: string): string => {
    return "custom-packing-name-normal";
  };

  const generateOrderNotesHTML = (
    order: PrintDeliveryOrder,
    copyType: "rider" | "customer"
  ): string => {

    console.log("Order notes check:", {
      id: order.id,
      ref: order.orderReference,
      orderNote: order.orderNote,
      packingNote: order.packingNote,
      paymentNote: order.paymentNote,
    });

    const notesHTML: string[] = [];

    if (order.orderNote) {
      notesHTML.push(`
        <div class="order-note-section">
          <div class="order-note-label">Order Note :</div>
          <div class="order-note-text">${order.orderNote}</div>
        </div>
      `);
    }

    if (order.packingNote) {
      notesHTML.push(`
        <div class="order-note-section">
          <div class="order-note-label">Packing Note :</div>
          <div class="order-note-text">${order.packingNote}</div>
        </div>
      `);
    }

    if (copyType === "rider" && order.paymentNote) {
      notesHTML.push(`
        <div class="order-note-section payment-note-section">
          <div class="order-note-label">Payment Note :</div>
          <div class="order-note-text">${order.paymentNote}</div>
        </div>
      `);
    }

    return notesHTML.join("");
  };

  // Generate packing list HTML for a single order with add-on items
  const generateOrderPackingListHTML = (
    order: PrintDeliveryOrder,
    copyType: "rider" | "customer" = "rider"
  ): string => {
    const packingListHTML: string[] = [];

    packingListHTML.push(`
  <div class="packing-section">
    <div class="packing-header">PACKING LIST</div>
    <div class="thin-divider"></div>
`);

    order.orderItems.forEach((item, idx) => {
      // Format display name with Tiny ID if available
      const formattedDisplayName = formatDisplayName(
        item.displayName,
        item.tinyId
      );
      const itemNameClass = getItemDisplayNameClass(formattedDisplayName);

      // Add main item with checkbox (only for rider copy)
      const showCheckbox = copyType === "rider";
      const itemClass = showCheckbox
        ? "packing-item"
        : "packing-item-no-checkbox";

      packingListHTML.push(`
    <div class="${itemClass}">
      <div class="packing-details">
        <div class="packing-name ${itemNameClass}">${
        idx + 1
      }. ${formattedDisplayName} -
          <span class="item-quantity">${item.quantity}</span>
        </div>
      </div>
      ${showCheckbox ? '<div class="checkbox"></div>' : ""}
    </div>
  `);

      // Add custom items if this is a CustomMenu
      if (
        item.itemType === "CustomMenu" &&
        item.customItems &&
        item.customItems.length > 0
      ) {
        // Separate non-addon and add-on items
        const nonAddOnItems = item.customItems.filter(
          (customItem) => !customItem.isAddOn
        );
        const addOnItems = item.customItems.filter(
          (customItem) => customItem.isAddOn
        );

        // Show non-addon items first
        nonAddOnItems.forEach((customItem: CustomItem) => {
          // Format custom item display name with Tiny ID if available
          const formattedCustomDisplayName = formatDisplayName(
            customItem.displayName,
            customItem.tinyId
          );
          const customItemNameClass = getCustomItemDisplayNameClass(
            formattedCustomDisplayName
          );

          const customItemClass =
            copyType === "rider"
              ? "custom-packing-item"
              : "custom-packing-item-no-checkbox";

          packingListHTML.push(`
        <div class="${customItemClass}">
          <div class="custom-packing-details">
            <div class="custom-packing-name ${customItemNameClass}">* ${formattedCustomDisplayName} -
              <span class="item-quantity"><strong>${customItem.quantity}</strong></span>
            </div>
          </div>
        </div>
      `);
        });

        // Show add-on items under the same custom menu
        if (addOnItems.length > 0) {
          // Add a separator for add-on items
          packingListHTML.push(`
          <div class="addon-separator">
            <div class="addon-header">ADD ON ITEMS</div>
          </div>
        `);

          // Group add-on items by name AND tinyId to avoid duplicates
          const groupedAddOns: Record<
            string,
            { quantity: number; tinyId?: number; displayName: string }
          > = {};

          addOnItems.forEach((customItem) => {
            // Create a unique key that includes both name and tinyId
            const key = `${customItem.displayName}_${
              customItem.tinyId || "no-id"
            }`;
            if (groupedAddOns[key]) {
              groupedAddOns[key].quantity += customItem.quantity;
            } else {
              groupedAddOns[key] = {
                quantity: customItem.quantity,
                tinyId: customItem.tinyId,
                displayName: customItem.displayName,
              };
            }
          });

          // Display grouped add-on items
          Object.entries(groupedAddOns).forEach(([key, data]) => {
            const formattedCustomDisplayName = formatDisplayName(
              data.displayName,
              data.tinyId
            );
            const customItemNameClass = getCustomItemDisplayNameClass(
              formattedCustomDisplayName
            );

            const customItemClass =
              copyType === "rider"
                ? "custom-packing-item addon-item"
                : "custom-packing-item-no-checkbox addon-item";

            packingListHTML.push(`
          <div class="${customItemClass}">
            <div class="custom-packing-details">
              <div class="custom-packing-name ${customItemNameClass}">* ${formattedCustomDisplayName} -
                <span class="item-quantity"> <strong>${data.quantity}</strong></span>
              </div>
            </div>
          </div>
        `);
          });
        }
      }

      if (idx < order.orderItems.length - 1) {
        packingListHTML.push(`<div class="dotted-divider"></div>`);
      }
    });

    packingListHTML.push(`</div>`);
    return packingListHTML.join("");
  };
  // Updated Consolidated packing list generator for multiple orders with add-on items
  const generateConsolidatedPackingListHTML = (
    orders: PrintDeliveryOrder[],
    copyType: "rider" | "customer" = "rider",
    isPandS: boolean = false
  ): string => {
    const packingListHTML: string[] = [];

    // Create a map to track parent items and their custom items
    const parentItemsMap = new Map<
      string,
      {
        displayName: string;
        tinyId?: number;
        quantity: number;
        isCustomItem: boolean;
        customItems: Map<
          string,
          {
            displayName: string;
            tinyId?: number;
            quantity: number;
            parentItemName: string;
            isAddOn?: boolean;
          }
        >;
      }
    >();

    // Process all orders
    orders.forEach((order) => {
      order.orderItems.forEach((item) => {
        // Create a unique key for the parent item
        const parentKey = createItemKey(item);

        // Get or create parent item entry
        let parentEntry = parentItemsMap.get(parentKey);

        if (!parentEntry) {
          parentEntry = {
            displayName: item.displayName,
            tinyId: item.tinyId,
            quantity: 0,
            isCustomItem: false,
            customItems: new Map(),
          };
          parentItemsMap.set(parentKey, parentEntry);
        }

        // Update parent quantity
        parentEntry.quantity += item.quantity;

        // Process custom items if this is a CustomMenu
        if (
          item.itemType === "CustomMenu" &&
          item.customItems &&
          item.customItems.length > 0
        ) {
          item.customItems.forEach((customItem) => {
            // Create a unique key for this custom item under its parent
            const customKey =
              createCustomItemKey(customItem) +
              (customItem.isAddOn ? "_addon" : "_regular");

            // Get or create custom item entry
            let customEntry = parentEntry!.customItems.get(customKey);

            if (!customEntry) {
              customEntry = {
                displayName: customItem.displayName,
                tinyId: customItem.tinyId,
                quantity: 0,
                parentItemName: item.displayName,
                isAddOn: customItem.isAddOn || false,
              };
              parentEntry!.customItems.set(customKey, customEntry);
            }

            // Update custom item quantity (custom item quantity * parent item quantity)
            customEntry.quantity += customItem.quantity * item.quantity;
          });
        }
      });
    });

    // Convert to arrays for sorting
    const sortedParentItems = Array.from(parentItemsMap.entries())
      .map(([key, data]) => ({ key, ...data }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    // Calculate total pack quantity for P&S
        const totalPackQty = orders.reduce((total, order) => {
          return total + order.orderItems.reduce((sum, item) => sum + item.quantity, 0);
        }, 0);

        // Generate HTML
        packingListHTML.push(`
          <div class="packing-section">
            <div class="packing-header">${isPandS ? "ITEM LIST" : "CONSOLIDATED PACKING LIST"}</div>
            <div class="thin-divider"></div>
        `);

    let itemCounter = 1;
    const showCheckbox = copyType === "rider";

    // Process each parent item and its custom items
    sortedParentItems.forEach((parentItem, parentIdx) => {
      // Format parent display name with Tiny ID
      const formattedParentDisplayName = formatDisplayName(
        parentItem.displayName,
        parentItem.tinyId
      );
      const parentNameClass = getItemDisplayNameClass(
        formattedParentDisplayName
      );

      // Add the parent item
      const parentItemClass = showCheckbox
        ? "packing-item"
        : "packing-item-no-checkbox";

      packingListHTML.push(`
    <div class="${parentItemClass}">
      <div class="packing-details">
        <div class="packing-name ${parentNameClass}">${itemCounter}. ${formattedParentDisplayName} -
          <span class="item-quantity">${parentItem.quantity}</span>
        </div>
      </div>
      ${showCheckbox ? '<div class="checkbox"></div>' : ""}
    </div>
  `);

      itemCounter++;

      // Separate non-addon and add-on custom items
      const customItemsArray = Array.from(parentItem.customItems.values());
      const nonAddOnItems = customItemsArray.filter((item) => !item.isAddOn);
      const addOnItems = customItemsArray.filter((item) => item.isAddOn);

      // Show non-addon items first
      if (nonAddOnItems.length > 0) {
        const sortedNonAddOnItems = nonAddOnItems.sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        );

        sortedNonAddOnItems.forEach((customItem) => {
          // Format custom item display name with Tiny ID
          const formattedCustomDisplayName = formatDisplayName(
            customItem.displayName,
            customItem.tinyId
          );
          const customItemNameClass = getCustomItemDisplayNameClass(
            formattedCustomDisplayName
          );

          const customItemClass = showCheckbox
            ? "custom-packing-item"
            : "custom-packing-item-no-checkbox";

          packingListHTML.push(`
        <div class="${customItemClass}">
          <div class="custom-packing-details">
            <div class="custom-packing-name ${customItemNameClass}">* ${formattedCustomDisplayName} -
              <span class="item-quantity"><strong>${customItem.quantity}</strong></span>
            </div>
          </div>
        </div>
      `);
        });
      }

      // Show add-on items under the same custom menu
      if (addOnItems.length > 0) {
        // Add a separator for add-on items
        packingListHTML.push(`
        <div class="addon-separator">
          <div class="addon-header">ADD ON ITEMS</div>
        </div>
      `);

        // Group add-on items by name AND tinyId to avoid duplicates
        const groupedAddOns: Record<
          string,
          { quantity: number; tinyId?: number; displayName: string }
        > = {};

        addOnItems.forEach((customItem) => {
          // Create a unique key that includes both name and tinyId
          const key = `${customItem.displayName}_${
            customItem.tinyId || "no-id"
          }`;
          if (groupedAddOns[key]) {
            groupedAddOns[key].quantity += customItem.quantity;
          } else {
            groupedAddOns[key] = {
              quantity: customItem.quantity,
              tinyId: customItem.tinyId,
              displayName: customItem.displayName,
            };
          }
        });

        // Display grouped add-on items
        Object.entries(groupedAddOns).forEach(([key, data]) => {
          const formattedCustomDisplayName = formatDisplayName(
            data.displayName,
            data.tinyId
          );
          const customItemNameClass = getCustomItemDisplayNameClass(
            formattedCustomDisplayName
          );

          const customItemClass = showCheckbox
            ? "custom-packing-item addon-item"
            : "custom-packing-item-no-checkbox addon-item";

          packingListHTML.push(`
        <div class="${customItemClass}">
          <div class="custom-packing-details">
            <div class="custom-packing-name ${customItemNameClass}">* ${formattedCustomDisplayName} -
              <span class="item-quantity"><strong>${data.quantity}</strong></span>
            </div>
          </div>
        </div>
      `);
        });
      }

      // Add divider if not the last item
      if (parentIdx < sortedParentItems.length - 1) {
        packingListHTML.push(`<div class="dotted-divider"></div>`);
      }
    });

    if (isPandS) {
      packingListHTML.push(`
        <div class="thin-divider"></div>

        <div class="pands-total-qty">
          Total Pack Quantity : ${totalPackQty}
        </div>

        <div style="height:4mm;"></div>

        <div class="pands-signature-item">
          <span class="signature-label">Issued By :   _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _</span>
          <span class="signature-line"></span>
        </div>

        <div style="height:4mm;"></div>

        <div class="pands-signature-item">
          <span class="signature-label">Received By : _ _ _ _ _ _ _ _ _ _ _ _ _ _ _</span>
          <span class="signature-line"></span>
        </div>

        <div style="height:2mm;"></div>
      `);
    }

    packingListHTML.push(`</div>`);
    return packingListHTML.join("");
  };

  // Generate HTML for specific order
  const generatePrintHTMLForOrder = (
    order: PrintDeliveryOrder,
    orderReference: string,
    copyType: "rider" | "customer" = "rider"
  ): string => {
    const orderListHTML: string[] = [];

    const currentDate = new Date();
    const dateStr = currentDate.toLocaleDateString("en-GB");
    const timeStr = currentDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // Get entity info for the order
    const entityInfo = getEntityInfo(order);
    const { cartDisplayName, riderName } = getCartInfo(order.id);

    if (copyType === "rider") {
      // RIDER COPY - Single order document
      orderListHTML.push(`
      <div class="header">
        <div class="logo-container">
          <img src="${LOGO_URL}" alt="logo" class="logo-image" />
        </div>
        <div class="header-title">ORDER BILL</div>
        <div class="header-subtitle">RIDER COPY</div>
      </div>
      
      <div class="divider"></div>
      
      <div class="info-section">
        <div class="info-line">Order Reference: ${orderReference}</div>
        <div class="info-line">Date: ${dateStr}</div>
        <div class="info-line">Time: ${timeStr}</div>
        ${
          cartDisplayName
            ? `<div class="info-line">Delivery Vehicle: ${cartDisplayName}</div>`
            : ""
        }
        ${
          riderName
            ? `<div class="info-line">Rider Name: ${riderName}</div>`
            : ""
        }
      </div>
      
      <div class="divider"></div>
    `);

      // Customer/Organization information
      if (entityInfo) {
        orderListHTML.push(`
        <div class="group-section">
          <div class="group-header">${entityInfo.type}: ${entityInfo.name}</div>
          <div class="thin-divider"></div>
          <div class="thin-divider"></div>
        </div>
      `);
      }

      // Order details
      orderListHTML.push(`
      <div class="order-section">
        <div class="order-ref-with-bg">ORDER REF: ${orderReference}</div>
        <div class="order-details">
          <div class="info-line">ETA: </div>
          <div class="info-line">Order Type: Individual</div>
          ${
            entityInfo
              ? `<div class="info-line">${entityInfo.label}: ${entityInfo.name}</div>`
              : ""
          }
        </div>
      </div>
    `);

      orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

      // Order items with prices and Tiny IDs
      order.orderItems.forEach((item, idx) => {
        // Format display name with Tiny ID if available
        const formattedItemName = formatDisplayName(
          item.displayName,
          item.tinyId
        );
        const itemNameClass = getItemDisplayNameClass(formattedItemName);
        const itemName = `${idx + 1}. ${formattedItemName}`;
        const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

        orderListHTML.push(`
        <div class="item-section">
          <div class="item-name ${itemNameClass}">${itemName}</div>
          <div class="item-details">
            <div class="item-qty">QTY: ${item.quantity} x Rs.${
          item.unitPrice
        }</div>
          </div>
          <div class="item-total">Rs.${itemTotal}</div>
          ${
            idx < order.orderItems.length - 1
              ? `<div class="dotted-divider"></div>`
              : ""
          }
        </div>
      `);
      });

      // Add order summary for rider copy
      if (order.paymentSummary) {
        const paymentSummary = order.paymentSummary;
        const paymentStatusHTML = order.paymentCompleted
          ? `<div class="summary-line paid-status">PAID</div>`
          : `<div class="summary-line unpaid-status"></div>`;

        const discountHTML =
          paymentSummary.discount && paymentSummary.discount > 0
            ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
            : "";

        orderListHTML.push(`
        <div class="order-summary">
          <div class="summary-line">Sub Total: Rs.${
            paymentSummary.totalPrice || order.totalPrice
          }</div>
          <div class="summary-line">Delivery Fee: Rs.${
            paymentSummary.deliveryFee || 0
          }</div>
          ${discountHTML}
          <div class="summary-line">Tax: Rs.${paymentSummary.tax || 0}</div>
          <div class="summary-line total-amount">Total Payable: Rs.${
            paymentSummary.netAmount ||
            (paymentSummary.totalPrice || order.totalPrice) +
              (paymentSummary.deliveryFee || 0) -
              (paymentSummary.discount || 0) +
              (paymentSummary.tax || 0)
          }</div>
          <div class="summary-line">Payment Method: ${
            order.paymentMethod || "N/A"
          }</div>
          ${paymentStatusHTML}
        </div>
      `);
      } else {
        orderListHTML.push(`
        <div class="order-summary">
          <div class="summary-line total-amount">Total Amount: Rs.${
            order.totalPrice
          }</div>
          <div class="summary-line">Payment Method: ${
            order.paymentMethod || "N/A"
          }</div>
          <div class="summary-line">${
            order.paymentCompleted ? "PAID" : ""
          }</div>
        </div>
      `);
      }

      // Add notes after payment breakdown
      orderListHTML.push(generateOrderNotesHTML(order, "rider"));
      orderListHTML.push(`<div class="divider"></div>`);

      // Add packing list for the order AFTER order summary
      orderListHTML.push(generateOrderPackingListHTML(order, "rider"));
      orderListHTML.push(`<div class="divider"></div>`);

      // Add rider footer
      orderListHTML.push(`
      <div class="footer">
        <div>SAFE RIDE</div>
      </div>
    `);
    } else {
      // CUSTOMER COPY
      orderListHTML.push(`
      <div class="header">
        <div class="logo-container">
          <img src="${LOGO_URL}" alt="logo" class="logo-image" />
        </div>
        <div class="header-title">ORDER BILL</div>
        <div class="header-subtitle">CUSTOMER COPY</div>
      </div>
      
      <div class="order-ref-with-bg">ORDER REFERENCE : ${orderReference}</div>
      
      <div class="info-section">
        <div class="info-line">Date : ${dateStr}</div>
        <div class="info-line">Time : ${timeStr}</div>
        ${
          cartDisplayName
            ? `<div class="info-line">Delivery Vehicle : ${cartDisplayName}</div>`
            : ""
        }
      </div>
      
      <div class="divider"></div>
    `);

      // Customer/Organization information
      if (entityInfo) {
        orderListHTML.push(`
        <div class="group-section">
          <div class="group-header">${entityInfo.type} : ${entityInfo.name}</div>
        </div>
      `);
      }

      // Order details
      orderListHTML.push(`
      <div class="order-section">
        <div class="order-details">
          <div class="info-line">ETA : </div>
          <div class="info-line">Order Type : Individual</div>
        </div>
      </div>
    `);

    const isPandS = order.organization?.displayName?.includes('P&S') ?? false;

    if (!isPandS) {
      orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

      // Order items with Tiny IDs without checkboxes for customer copy
      order.orderItems.forEach((item, idx) => {
        // Format display name with Tiny ID if available
        const formattedItemName = formatDisplayName(
          item.displayName,
          item.tinyId
        );
        const itemNameClass = getItemDisplayNameClass(formattedItemName);
        const itemName = `${idx + 1}. ${formattedItemName}`;
        const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

        orderListHTML.push(`
        <div class="item-section">
          <div class="item-name ${itemNameClass}">${itemName}</div>
          <div class="item-details">
            <div class="item-qty">QTY: ${item.quantity} x Rs.${
          item.unitPrice
        }</div>
          </div>
          <div class="item-total">Rs.${itemTotal}</div>
          ${
            idx < order.orderItems.length - 1
              ? `<div class="dotted-divider"></div>`
              : ""
          }
        </div>
      `);
      });
    }

    if (!isPandS) {
      // Add order summary for customer copy
      if (order.paymentSummary) {
        const paymentSummary = order.paymentSummary;
        const paymentStatusHTML = order.paymentCompleted
          ? `<div class="summary-line paid-status">PAID</div>`
          : `<div class="summary-line unpaid-status"></div>`;

        const discountHTML =
          paymentSummary.discount && paymentSummary.discount > 0
            ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
            : "";

        orderListHTML.push(`
        <div class="order-summary">
          <div class="summary-line">Sub Total: Rs.${
            paymentSummary.totalPrice || order.totalPrice
          }</div>
          <div class="summary-line">Delivery Fee: Rs.${
            paymentSummary.deliveryFee || 0
          }</div>
          ${discountHTML}
          <div class="summary-line">Tax: Rs.${paymentSummary.tax || 0}</div>
          <div class="summary-line total-amount">Total Payable: Rs.${
            paymentSummary.netAmount ||
            (paymentSummary.totalPrice || order.totalPrice) +
              (paymentSummary.deliveryFee || 0) -
              (paymentSummary.discount || 0) +
              (paymentSummary.tax || 0)
          }</div>
          <div class="summary-line">Payment Method: ${
            order.paymentMethod || "N/A"
          }</div>
          ${paymentStatusHTML}
        </div>
      `);
      } else {
        orderListHTML.push(`
        <div class="order-summary">
          <div class="summary-line total-amount">Total Amount: Rs.${
            order.totalPrice
          }</div>
          <div class="summary-line">Payment Method: ${
            order.paymentMethod || "N/A"
          }</div>
          <div class="summary-line">${
            order.paymentCompleted ? "PAID" : ""
          }</div>
        </div>
      `);
      }
    }

      // Add notes after payment breakdown
      if (!isPandS) {
        orderListHTML.push(generateOrderNotesHTML(order, "customer"));
      }
      orderListHTML.push(`<div class="divider"></div>`);

      // Add packing list without checkboxes for customer copy AFTER order summary
      orderListHTML.push(generateOrderPackingListHTML(order, "customer"));
      orderListHTML.push(`<div class="divider"></div>`);

      // Add customer footer
      orderListHTML.push(`
      <div class="footer">
        <div>Thank you for your order!</div>
        <div>55, 72 Vauxhall Ln, Colombo 00200</div>
        <div>HOTLINE: 011 444 1333</div>
      </div>
      <div class="cutting-line"></div>
      <div class="footer-spacing"></div>
    `);
    }

    // Return the HTML structure with updated CSS
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Order ${
    copyType === "rider" ? "Rider" : "Customer"
  } Copy - ${orderReference}</title>
  <style>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  @page {
    size: 80mm auto;
    margin: 0;
    padding: 0;
  }
  
  html, body {
    direction: ltr !important;
    unicode-bidi: normal !important;
    text-align: left !important;
  }
  
  @media print {
    html, body {
      width: 80mm !important;
      min-width: 80mm !important;
      max-width: 80mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 0 !important;
      font-family: 'Arial Narrow', Arial, sans-serif !important;
      font-size: 10pt !important;
      line-height: 1.2 !important;
      background: white !important;
      color: black !important;
      direction: ltr !important;
      overflow: visible !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }
    
    .thermal-content {
      width: 76mm !important;
      min-width: 76mm !important;
      max-width: 76mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 1mm 2mm !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }
    
    .content-wrapper {
      width: 100% !important;
      display: block !important;
      margin: 0 auto !important;
    }

.tiny-id-highlight {
  font-weight: bold;
  font-size: 10.5pt; /* Slightly larger than normal text */
  color: #000; /* Keep it black */
  margin-left: 0.5mm;
} 

.packing-name-normal {
  font-size: 9pt;
  font-weight: bold;
  text-align: left;
  flex: 1;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 52mm;
}

.custom-packing-name-normal {
  font-size: 8pt;
  text-align: left;
  flex: 1;
  color: black;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 52mm;
}

    .no-print { 
      display: none !important; 
    }
  }
  
  body {
    font-family: 'Arial Narrow', Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.2;
    width: 80mm;
    min-width: 80mm;
    max-width: 80mm;
    height: auto;
    margin: 0 auto;
    padding: 0;
    background: white;
    color: black;
    overflow: visible;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .thermal-content {
    width: 76mm;
    min-width: 76mm;
    max-width: 76mm;
    height: auto;
    margin: 0 auto;
    padding: 1mm 2mm;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .content-wrapper {
    width: 100%;
    display: block;
    margin: 0 auto;
  }
  
  .header {
    text-align: center;
    margin: 0 0 2mm 0;
    padding: 0;
    width: 100%;
  }
  
  .logo-container {
    margin: 0 0 1mm 0;
    text-align: center;
    width: 100%;
  }
  
  .logo-image {
    max-width: 25mm;
    max-height: 12mm;
    height: auto;
    display: inline-block;
  }
  
  .header-title {
    font-weight: bold;
    font-size: 11pt;
    margin: 0 0 0.5mm 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
    width: 100%;
  }
  
  .header-subtitle {
    font-weight: bold;
    font-size: 9pt;
    margin: 0 0 1mm 0;
    text-transform: uppercase;
    text-align: center;
    width: 100%;
  }
  
  .info-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .info-line {
    font-size: 8pt;
    margin: 0.5mm 0;
    font-weight: normal;
    text-align: left;
    width: 100%;
  }
  
  .divider {
    border-top: 1px solid #000;
    margin: 2mm 0;
    height: 0;
    width: 100%;
  }
  
  .thin-divider {
    border-top: 0.5px solid #666;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .dotted-divider {
    border-top: 1px dotted #999;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .cutting-line {
    border-top: 2px dashed #000;
    margin: 3mm 0;
    height: 0;
    width: 100%;
  }
  
  .group-section {
    margin: 2mm 0;
    width: 100%;
  }
  
  .order-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .item-section {
    margin: 0.5mm 0;
    width: 100%;
  }
  
  .packing-section {
    margin: 2mm 0;
    padding: 1mm;
    border: 0.5px solid #dee2e6;
    width: 100%;
    overflow: hidden;
    word-wrap: break-word;
  }
  
  .order-spacing {
    height: 1mm;
    width: 100%;
  }
  
  .group-header {
    font-weight: bold;
    font-size: 8pt;
    margin: 0.3mm 0;
    padding: 0;
    text-align: left;
    text-transform: uppercase;
    width: 100%;
  }
  
  .order-ref-with-bg {
    font-weight: bold;
    font-size: 9pt;
    margin: 0.5mm 0;
    padding: 1mm;
    text-align: left;
    text-transform: uppercase;
    background-color: black;
    color: #fff;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .packing-header {
    font-weight: bold;
    font-size: 9pt;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 1mm;
color:black;    width: 100%;
  }
  
  .order-details {
    margin: 0.5mm 0 1mm 0;
    width: 100%;
  }
  
  .item-name {
    font-size: 9pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: left;
    width: 100%;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
  }
  
  .item-details {
    margin: 0.3mm 0 0 1mm;
    width: 100%;
  }
  
  .item-qty {
    font-size: 8pt;
    margin: 0.2mm 0;
    font-weight: bold;
    text-align: left;
    width: 100%;
  }
  
  .item-total {
    font-size: 8pt;
    margin: 0.2mm 0;
    font-weight: bold;
    text-align: right;
    width: 100%;
    color: #333;
  }
  
  /* Add-on separator styles */
  .addon-separator {
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.2mm 0;
    font-size: 7pt;
    width: 100%;
    overflow: hidden;
  }
  
  .addon-header {
    font-weight: bold;
    font-style: italic;
color:black;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .addon-item {
    margin-left: 4mm !important;
  }
  
  .order-summary {
    margin: 1mm 0;
    padding: 1mm;
    background-color: #f5f5f5;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .summary-line {
    font-size: 8pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: right;
    width: 100%;
  }
  
  .total-amount {
    font-size: 9pt;
    font-weight: bolder;
    color: #000;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    margin-top: 0.5mm;
  }
  
  .paid-status {
    color: #28a745;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  .unpaid-status {
    color: #dc3545;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  /* Packing List Styles - FIXED FOR TEXT WRAPPING */
  .packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
    overflow: hidden;
  }
  
  .packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
    overflow: hidden;
  }
  
  .custom-packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
    overflow: hidden;
  }
  
  .custom-packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
    overflow: hidden;
  }
  
  .packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
    min-width: 0;
    overflow: hidden;
  }
  
  .custom-packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
    min-width: 0;
    overflow: hidden;
  }
  
  /* Dynamic font sizes for packing names based on length */
  .packing-name-normal {
    font-size: 9pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 52mm;
  }
  
  .packing-name-medium {
    font-size: 8.5pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 50mm;
  }
  
  .packing-name-long {
    font-size: 8pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 48mm;
  }
  
  .packing-name-very-long {
    font-size: 7.5pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 46mm;
  }
  
  .custom-packing-name-normal {
    font-size: 8pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 52mm;
  }
  
  .custom-packing-name-medium {
    font-size: 7.5pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 50mm;
  }
  
  .custom-packing-name-long {
    font-size: 7pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 48mm;
  }
  
  .custom-packing-name-very-long {
    font-size: 6.5pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 46mm;
  }
  
  .packing-qty {
    font-size: 9pt;
    font-weight: bold;
    text-align: right;
    min-width: 5mm;
    flex-shrink: 0;
    margin-left: 1mm;
  }
  
  .custom-packing-qty {
    font-size: 8pt;
    text-align: right;
    min-width: 5mm;
color:black;
    flex-shrink: 0;
    margin-left: 1mm;
  }
  
  .checkbox {
    width: 3mm;
    height: 3mm;
    border: 1px solid #000;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .custom-checkbox {
    width: 2.5mm;
    height: 2.5mm;
    border: 1px solid #666;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .footer {
    text-align: center;
    font-size: 8pt;
    margin: 3mm 0 0 0;
    padding: 1mm 0 0 0;
    border-top: 1px solid #000;
    font-weight: bold;
    width: 100%;
  }
  
  .footer-spacing {
    height: 5mm;
    margin: 2mm 0;
    width: 100%;
  }

  .pands-total-qty {
    font-size: 9pt;
    font-weight: bold;
    text-align: right;
    margin-top: 2mm;
    margin-bottom: 4mm;
  }

  .pands-signature-section {
    display: flex;
    flex-direction: column;
    gap: 4mm;
    margin-bottom: 2mm;
  }

  .pands-signature-item {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .signature-label {
    font-size: 8pt;
    font-weight: bold;
    min-width: 22mm;
  }

  .signature-line {
    flex-grow: 1;
    min-width: 50mm;
    border-bottom: 1px solid #000;
  }

  .order-note-section {
    margin: 1mm 0;
    padding: 1mm;
    border: 0.5px dashed #888;
    width: 100%;
  }

  .order-note-label {
    font-size: 7.5pt;
    font-weight: bold;
    text-align: left;
    margin-bottom: 0.3mm;
    text-transform: uppercase;
  }

  .order-note-text {
    font-size: 7.5pt;
    font-weight: normal;
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
  }

  .payment-note-section {
    border-style: solid;
    border-color: #333;
  }
  
  @media print {
    .footer-spacing {
      height: 5mm !important;
      margin: 2mm 0 !important;
    }
    
    .cutting-line {
      border-top: 2px dashed #000 !important;
      margin: 3mm 0 !important;
    }
    
    .packing-name-normal,
    .packing-name-medium,
    .packing-name-long,
    .packing-name-very-long,
    .custom-packing-name-normal,
    .custom-packing-name-medium,
    .custom-packing-name-long,
    .custom-packing-name-very-long {
      max-width: 52mm !important;
      word-break: break-word !important;
    }
    
    .packing-item,
    .packing-item-no-checkbox,
    .custom-packing-item,
    .custom-packing-item-no-checkbox {
      page-break-inside: avoid !important;
    }
  }
</style>
</head>
<body>
  <div class="content-wrapper">
    <div class="thermal-content">
      ${orderListHTML.join("")}
    </div>
  </div>
</body>
</html>
    `;
  };

  // Print single order
  const printOrder = async ({
    orderId,
    orderReference,
    copyType,
  }: PrintByOrderOptions): Promise<void> => {
    setIsPrinting(true);
    try {
      // Find the order in deliveries
      const order = deliveries?.find((d) => d.id === orderId);

      if (!order) {
        console.warn(`Order not found: ${orderId}`);
        return;
      }

      const htmlContent = generatePrintHTMLForOrder(
        order,
        orderReference,
        copyType
      );

      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.style.opacity = "0";
      iframe.style.top = "-9999px";
      iframe.style.left = "-9999px";
      iframe.id = "thermal-print-frame";

      iframe.onload = () => {
        setTimeout(() => {
          try {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();

              const cleanup = () => {
                setTimeout(() => {
                  if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                  }
                  URL.revokeObjectURL(url);
                }, 100);
              };

              iframe.contentWindow.addEventListener("afterprint", cleanup);

              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                  URL.revokeObjectURL(url);
                }
              }, 5000);
            }
          } catch (error) {
            console.error("Print error:", error);
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            URL.revokeObjectURL(url);
          }
        }, 500);
      };

      iframe.src = url;
      document.body.appendChild(iframe);
    } catch (error) {
      console.error("Error printing order:", error);
      throw error;
    } finally {
      setIsPrinting(false);
    }
  };

  // Print multiple orders
  const printMultipleOrders = async (
    orders: PrintByOrderOptions[]
  ): Promise<void> => {
    setIsPrinting(true);
    try {
      for (const orderOptions of orders) {
        await printOrder(orderOptions);
        // Add a small delay between prints to ensure proper sequencing
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error("Error printing multiple orders:", error);
      throw error;
    } finally {
      setIsPrinting(false);
    }
  };

  // Generate HTML for specific group
  const generatePrintHTMLForGroup = (
    group: Group,
    groupName: string,
    copyType: "rider" | "customer" = "rider"
  ): string => {
    const orderListHTML: string[] = [];

    const currentDate = new Date();
    const dateStr = currentDate.toLocaleDateString("en-GB");
    const timeStr = currentDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // Get cart info for this group
    const cartInfo = group.cart
      ? vehicles.find((v) => v.id === group.cart?.cartId)
      : null;

    const cartDisplayNameForGroup = group.cart?.displayName || "";

    // Get orders for this specific group
    const groupOrders = group.deliveryIds
      .map((deliveryId) => deliveries?.find((d) => d.id === deliveryId))
      .filter((order): order is PrintDeliveryOrder => isDefined(order));

    if (copyType === "rider") {
      // RIDER COPY - Single document for the group
      orderListHTML.push(`
        <div class="header">
          <div class="logo-container">
            <img src="${LOGO_URL}" alt="logo" class="logo-image" />
          </div>
          <div class="header-title">ORDER LIST</div>
          <div class="header-subtitle">RIDER COPY - ${groupName}</div>
          ${cartDisplayNameForGroup ? `<div class="header-subtitle">${cartDisplayNameForGroup}</div>` : ""}
        </div>
        
        <div class="divider"></div>
        
        <div class="info-section">
          <div class="info-line">Date : ${dateStr}</div>
          <div class="info-line">Time : ${timeStr}</div>
          ${
            group.deliveryRoute
              ? `<div class="info-line">Distance : ${formatDistance(
                  group.deliveryRoute.distanceInKm
                )}</div>`
              : ""
          }
        </div>
      `);

      // Group orders by customer/organization for rider copy
      const customerOrdersMap = new Map<
        string,
        {
          entityId: string;
          entityName: string;
          entityType: "CUSTOMER" | "ORGANIZATION";
          entityLabel: string;
          orders: Array<{
            id: string;
            orderReference: string;
            orderItems: BaseOrderItem[];
            phoneNumber?: string;
            address?: string;
            paymentSummary?: any;
            totalPrice?: number;
            paymentCompleted?: boolean;
            paymentMethod?: string;
            originalOrder: PrintDeliveryOrder;
          }>;
        }
      >();

      // Collect orders without customer/organization info
      const ordersWithoutEntity: Array<{
        id: string;
        orderReference: string;
        orderItems: BaseOrderItem[];
        phoneNumber?: string;
        address?: string;
        paymentSummary?: any;
        totalPrice?: number;
        paymentCompleted?: boolean;
        paymentMethod?: string;
        originalOrder: PrintDeliveryOrder;
      }> = [];

      // Process all orders for this group
      groupOrders.forEach((order) => {
        const entityInfo = getEntityInfo(order);

        if (entityInfo) {
          const mapKey = `${entityInfo.type}_${entityInfo.id}`;

          if (!customerOrdersMap.has(mapKey)) {
            customerOrdersMap.set(mapKey, {
              entityId: entityInfo.id,
              entityName: entityInfo.name,
              entityType: entityInfo.type,
              entityLabel: entityInfo.label,
              orders: [],
            });
          }

          customerOrdersMap.get(mapKey)!.orders.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        } else {
          // Add to orders without customer/organization
          ordersWithoutEntity.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        }
      });

      // Display orders grouped by customer/organization
      if (customerOrdersMap.size > 0) {
        customerOrdersMap.forEach((entityData) => {

          entityData.orders.forEach((order, orderIndex) => {
            const orderType = "Group";

            orderListHTML.push(`
              <div class="order-section">
                <div class="order-ref-with-bg">ORDER REFERENCE : ${order.orderReference}</div>
                <div class="order-details">
                  <div class="info-line">ETA : </div>
                  <div class="info-line">Order Type : ${orderType}</div>
                  <div class="info-line">${entityData.entityLabel} : ${entityData.entityName}</div>
                  ${entityData.orders[0]?.phoneNumber ? `<div class="info-line">Contact Number : ${entityData.orders[0].phoneNumber}</div>` : ""}
                </div>
              </div>
            `);

            orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

            // Order items with checkboxes and Tiny IDs for rider copy
            order.orderItems.forEach((item, idx) => {
              // Format display name with Tiny ID if available
              const formattedItemName = formatDisplayName(
                item.displayName,
                item.tinyId
              );
              const itemNameClass = getItemDisplayNameClass(formattedItemName);
              const itemName = `${idx + 1}. ${formattedItemName}`;
              const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

              orderListHTML.push(`
                <div class="item-section">
                  <div class="item-name ${itemNameClass}">${itemName}</div>
                  <div class="item-details">
                    <div class="item-qty">QTY: ${item.quantity} x Rs.${
                item.unitPrice
              }</div>
                  </div>
                  <div class="item-total">Rs.${itemTotal}</div>
                  ${
                    idx < order.orderItems.length - 1
                      ? `<div class="dotted-divider"></div>`
                      : ""
                  }
                </div>
              `);
            });

            // Add order summary for rider copy with discount
            if (order.paymentSummary) {
              const paymentSummary = order.paymentSummary;
              const paymentStatusHTML = order.paymentCompleted
                ? `<div class="summary-line paid-status">PAID</div>`
                : "";

              const discountHTML =
                paymentSummary.discount && paymentSummary.discount > 0
                  ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                  : "";

              orderListHTML.push(`
                <div class="order-summary">
                  <div class="summary-line">Total Price: Rs.${
                    paymentSummary.totalPrice || order.totalPrice
                  }</div>
                  <div class="summary-line">Delivery Fee: Rs.${
                    paymentSummary.deliveryFee || 0
                  }</div>
                  ${discountHTML}
                  <div class="summary-line">Total Payable: Rs.${
                    paymentSummary.netAmount ||
                    paymentSummary.totalPrice +
                      (paymentSummary.deliveryFee || 0) -
                      (paymentSummary.discount || 0)
                  }</div>
                  <div class="summary-line">Payment Method: ${
                    order.paymentMethod || "N/A"
                  }</div>
                  ${paymentStatusHTML}
                </div>
              `);
            }

            // Add notes after payment breakdown
            orderListHTML.push(generateOrderNotesHTML(order.originalOrder, copyType));
            orderListHTML.push(`<div class="divider"></div>`);

            if (orderIndex < entityData.orders.length - 1) {
              orderListHTML.push(`<div class="order-spacing"></div>`);
            }
          });
        });
      }

      // Display orders without customer/organization info
      if (ordersWithoutEntity.length > 0) {
        orderListHTML.push(`
          <div class="group-section">
            <div class="group-header">OTHER ORDERS</div>
            <div class="thin-divider"></div>
            <div class="thin-divider"></div>
          </div>
        `);

        ordersWithoutEntity.forEach((order, orderIndex) => {
          const orderType = "Group";

          orderListHTML.push(`
            <div class="order-section">
              <div class="order-ref-with-bg">ORDER REF: ${order.orderReference}</div>
              <div class="order-details">
                <div class="info-line">ETA: </div>
                <div class="info-line">Order Type: ${orderType}</div>
              </div>
            </div>
          `);

          orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

          // Order items with checkboxes and Tiny IDs for rider copy
          order.orderItems.forEach((item, idx) => {
            // Format display name with Tiny ID if available
            const formattedItemName = formatDisplayName(
              item.displayName,
              item.tinyId
            );
            const itemNameClass = getItemDisplayNameClass(formattedItemName);
            const itemName = `${idx + 1}. ${formattedItemName}`;
            const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

            orderListHTML.push(`
              <div class="item-section">
                <div class="item-name ${itemNameClass}">${itemName}</div>
                <div class="item-details">
                  <div class="item-qty">QTY: ${item.quantity} x Rs.${
              item.unitPrice
            }</div>
                </div>
                <div class="item-total">Rs.${itemTotal}</div>
                ${
                  idx < order.orderItems.length - 1
                    ? `<div class="dotted-divider"></div>`
                    : ""
                }
              </div>
            `);
          });

          // Add order summary for rider copy with discount
          if (order.paymentSummary) {
            const paymentSummary = order.paymentSummary;
            const paymentStatusHTML = order.paymentCompleted
              ? `<div class="summary-line paid-status">PAID</div>`
              : "";

            const discountHTML =
              paymentSummary.discount && paymentSummary.discount > 0
                ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                : "";

            orderListHTML.push(`
              <div class="order-summary">
                <div class="summary-line">Total Price: Rs.${
                  paymentSummary.totalPrice || order.totalPrice
                }</div>
                <div class="summary-line">Delivery Fee: Rs.${
                  paymentSummary.deliveryFee || 0
                }</div>
                ${discountHTML}
                <div class="summary-line">Total Payable: Rs.${
                  paymentSummary.netAmount ||
                  paymentSummary.totalPrice +
                    (paymentSummary.deliveryFee || 0) -
                    (paymentSummary.discount || 0)
                }</div>
                <div class="summary-line">Payment Method: ${
                  order.paymentMethod || "N/A"
                }</div>
                ${paymentStatusHTML}
              </div>
            `);
          }

          // Add notes after payment breakdown
          orderListHTML.push(generateOrderNotesHTML(order.originalOrder, "rider"));
          orderListHTML.push(`<div class="divider"></div>`);

          if (orderIndex < ordersWithoutEntity.length - 1) {
            orderListHTML.push(`<div class="order-spacing"></div>`);
          }
        });
      }

      // Add consolidated packing list at the END for rider copy
            const isGroupPandS = groupOrders[0]?.organization?.displayName?.includes('P&S') ?? false;
            orderListHTML.push(
              generateConsolidatedPackingListHTML(groupOrders, "rider", isGroupPandS)
            );
      orderListHTML.push(`<div class="divider"></div>`);

      // Add rider footer
      orderListHTML.push(`
        <div class="footer">
          <div>SAFE RIDE</div>
        </div>
      `);
    } else {
      // CUSTOMER COPY - Group orders by customer with all orders together
      const customerOrdersMap = new Map<
        string,
        {
          entityId: string;
          entityName: string;
          entityType: "CUSTOMER" | "ORGANIZATION";
          entityLabel: string;
          orders: Array<{
            id: string;
            orderReference: string;
            orderItems: BaseOrderItem[];
            phoneNumber?: string;
            address?: string;
            paymentSummary?: any;
            totalPrice?: number;
            paymentCompleted?: boolean;
            paymentMethod?: string;
            originalOrder: PrintDeliveryOrder;
          }>;
        }
      >();

      // Collect orders without customer/organization info
      const ordersWithoutEntity: Array<{
        id: string;
        orderReference: string;
        orderItems: BaseOrderItem[];
        phoneNumber?: string;
        address?: string;
        paymentSummary?: any;
        totalPrice?: number;
        paymentCompleted?: boolean;
        paymentMethod?: string;
        originalOrder: PrintDeliveryOrder;
      }> = [];

      // Process all orders for this group
      groupOrders.forEach((order) => {
        const entityInfo = getEntityInfo(order);

        if (entityInfo) {
          const mapKey = `${entityInfo.type}_${entityInfo.id}`;

          if (!customerOrdersMap.has(mapKey)) {
            customerOrdersMap.set(mapKey, {
              entityId: entityInfo.id,
              entityName: entityInfo.name,
              entityType: entityInfo.type,
              entityLabel: entityInfo.label,
              orders: [],
            });
          }

          customerOrdersMap.get(mapKey)!.orders.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        } else {
          // Add to orders without customer/organization
          ordersWithoutEntity.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        }
      });

      // Generate header HTML that will be repeated for each customer
      const generateCustomerHeaderHTML = (customerIndex: number) => `
        ${customerIndex > 0 ? `<div class="page-break-spacing"></div>` : ""}
        <div class="header">
          <div class="logo-container">
            <img src="${LOGO_URL}" alt="logo" class="logo-image" />
          </div>
          <div class="header-title">ORDER LIST - ${groupName}</div>
          <div class="header-subtitle">CUSTOMER COPY</div>
        </div>
        
        <div class="divider"></div>
        
        <div class="info-section">
          ${
            cartInfo
              ? `<div class="info-line">Delivery Vehicle: ${cartInfo.name}</div>`
              : ""
          }
          <div class="info-line">Date : ${dateStr}</div>
          <div class="info-line">Time : ${timeStr}</div>
        </div>
      `;

      // Generate footer HTML that will be repeated for each customer
      const generateCustomerFooterHTML = () => `
        <div class="footer">
          <div>Thank you</div>
          <div>55, 72 Vauxhall Ln, Colombo 00200</div>
          <div>HOTLINE: 011 444 1333</div>
        </div>
        <div class="cutting-line"></div>
        <div class="footer-spacing"></div>
      `;

      if (customerOrdersMap.size > 0) {
        let customerIndex = 0;
        customerOrdersMap.forEach((entityData) => {
          // Add header for each customer
          orderListHTML.push(generateCustomerHeaderHTML(customerIndex));

          entityData.orders.forEach((order, orderIndex) => {
            const orderType = "Group";

            orderListHTML.push(`
              <div class="order-section">
                <div class="order-ref-with-bg">ORDER REFERENCE : ${order.orderReference}</div>
                <div class="order-details">
                  <div class="info-line">ETA : </div>
                  <div class="info-line">Order Type : ${orderType}</div>
                  <div class="info-line">${entityData.entityLabel} : ${entityData.entityName}</div>
                  ${entityData.orders[0]?.phoneNumber ? `<div class="info-line">Contact Number : ${entityData.orders[0].phoneNumber}</div>` : ""}
                </div>
              </div>
            `);

            const isPandS = order.originalOrder?.organization?.displayName?.includes('P&S') ?? false;

            if (!isPandS) {
              orderListHTML.push(`
                <div class="items-section">
                  <div class="thin-divider"></div>
                  <div class="packing-header">ORDER ITEMS</div>
                  <div class="thin-divider"></div>
                </div>
              `);

              order.orderItems.forEach((item, idx) => {
                const formattedItemName = formatDisplayName(item.displayName, item.tinyId);
                const itemNameClass = getItemDisplayNameClass(formattedItemName);
                const itemName = `${idx + 1}. ${formattedItemName}`;
                const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

                orderListHTML.push(`
                  <div class="item-section">
                    <div class="item-name ${itemNameClass}">${itemName}</div>
                    <div class="item-details">
                      <div class="item-qty">QTY: ${item.quantity} x Rs.${item.unitPrice}</div>
                    </div>
                    <div class="item-total">Rs.${itemTotal}</div>
                    ${idx < order.orderItems.length - 1 ? `<div class="dotted-divider"></div>` : ""}
                  </div>
                `);
              });

              if (order.paymentSummary) {
                const paymentSummary = order.paymentSummary;
                const paymentStatusHTML = order.paymentCompleted
                  ? `<div class="summary-line paid-status">PAID</div>`
                  : "";

                const discountHTML =
                  paymentSummary.discount && paymentSummary.discount > 0
                    ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                    : "";

                orderListHTML.push(`
                  <div class="order-summary">
                    <div class="summary-line">Total Price: Rs.${paymentSummary.totalPrice || order.totalPrice}</div>
                    <div class="summary-line">Delivery Fee: Rs.${paymentSummary.deliveryFee || 0}</div>
                    ${discountHTML}
                    <div class="summary-line">Total Payable: Rs.${
                      paymentSummary.netAmount ||
                      paymentSummary.totalPrice +
                        (paymentSummary.deliveryFee || 0) -
                        (paymentSummary.discount || 0)
                    }</div>
                    <div class="summary-line">Payment Method: ${order.paymentMethod || "N/A"}</div>
                    ${paymentStatusHTML}
                  </div>
                `);
              }
            }

          // Add notes after payment breakdown (skip for P&S customer copy)
            const isOrderPandS = order.originalOrder?.organization?.displayName?.includes('P&S') ?? false;
            if (!isOrderPandS) {
              orderListHTML.push(generateOrderNotesHTML(order.originalOrder, copyType));
            }
            orderListHTML.push(`<div class="divider"></div>`);

            if (orderIndex < entityData.orders.length - 1) {
              orderListHTML.push(`<div class="order-spacing"></div>`);
            }
          });

          // Add consolidated packing list WITHOUT checkboxes for customer copy
          const isEntityPandS = entityData.orders[0]?.originalOrder?.organization?.displayName?.includes('P&S') ?? false;
                    orderListHTML.push(
                      generateConsolidatedPackingListHTML(
                        entityData.orders.map((o) => o.originalOrder),
                        "customer",
                        isEntityPandS
                      )
                    );
          orderListHTML.push(`<div class="divider"></div>`);

          // Add footer for each customer
          orderListHTML.push(generateCustomerFooterHTML());

          // Add page break between customers (except for the last one)
          if (
            customerIndex < customerOrdersMap.size - 1 ||
            ordersWithoutEntity.length > 0
          ) {
            orderListHTML.push(`
              <div style="page-break-after: always;"></div>
            `);
          }
          customerIndex++;
        });
      }

      // Display orders without customer/organization info
      if (ordersWithoutEntity.length > 0) {
        // Add header for "OTHER ORDERS" section
        orderListHTML.push(generateCustomerHeaderHTML(customerOrdersMap.size));

        orderListHTML.push(`
          <div class="group-section">
            <div class="group-header">OTHER ORDERS</div>
            <div class="thin-divider"></div>
            <div class="thin-divider"></div>
          </div>
        `);

        ordersWithoutEntity.forEach((order, orderIndex) => {
          const orderType = "Group";

          orderListHTML.push(`
            <div class="order-section">
              <div class="order-ref-with-bg">ORDER REF: ${order.orderReference}</div>
              <div class="order-details">
                <div class="info-line">ETA: </div>
                <div class="info-line">Order Type: ${orderType}</div>
              </div>
            </div>
          `);

          orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

          // Order items with prices and Tiny IDs
          order.orderItems.forEach((item, idx) => {
            // Format display name with Tiny ID if available
            const formattedItemName = formatDisplayName(
              item.displayName,
              item.tinyId
            );
            const itemNameClass = getItemDisplayNameClass(formattedItemName);
            const itemName = `${idx + 1}. ${formattedItemName}`;
            const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

            orderListHTML.push(`
              <div class="item-section">
                <div class="item-name ${itemNameClass}">${itemName}</div>
                <div class="item-details">
                  <div class="item-qty">QTY: ${item.quantity} x Rs.${
              item.unitPrice
            }</div>
                </div>
                <div class="item-total">Rs.${itemTotal}</div>
                ${
                  idx < order.orderItems.length - 1
                    ? `<div class="dotted-divider"></div>`
                    : ""
                }
              </div>
            `);
          });

          // Add order summary for customer copy with discount
          if (order.paymentSummary) {
            const paymentSummary = order.paymentSummary;
            const paymentStatusHTML = order.paymentCompleted
              ? `<div class="summary-line paid-status">PAID</div>`
              : "";

            const discountHTML =
              paymentSummary.discount && paymentSummary.discount > 0
                ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                : "";

            orderListHTML.push(`
              <div class="order-summary">
                <div class="summary-line">Total Price: Rs.${
                  paymentSummary.totalPrice || order.totalPrice
                }</div>
                <div class="summary-line">Delivery Fee: Rs.${
                  paymentSummary.deliveryFee || 0
                }</div>
                ${discountHTML}
                <div class="summary-line">Total Payable: Rs.${
                  paymentSummary.netAmount ||
                  paymentSummary.totalPrice +
                    (paymentSummary.deliveryFee || 0) -
                    (paymentSummary.discount || 0)
                }</div>
                <div class="summary-line">Payment Method: ${
                  order.paymentMethod || "N/A"
                }</div>
                ${paymentStatusHTML}
              </div>
            `);
          }

          // Add notes after payment breakdown
          orderListHTML.push(generateOrderNotesHTML(order.originalOrder, "customer"));
          orderListHTML.push(`<div class="divider"></div>`);

          if (orderIndex < ordersWithoutEntity.length - 1) {
            orderListHTML.push(`<div class="order-spacing"></div>`);
          }
        });

        // Add consolidated packing list for "OTHER ORDERS" section
        orderListHTML.push(
          generateConsolidatedPackingListHTML(
            ordersWithoutEntity.map((o) => o.originalOrder),
            "customer"
          )
        );
        orderListHTML.push(`<div class="divider"></div>`);

        // Add footer for "OTHER ORDERS" section
        orderListHTML.push(generateCustomerFooterHTML());
      }

      if (customerOrdersMap.size === 0 && ordersWithoutEntity.length === 0) {
        orderListHTML.push(generateCustomerHeaderHTML(0));
        orderListHTML.push(`
          <div class="group-section">
            <div class="group-header">NO ORDERS AVAILABLE</div>
            <div class="thin-divider"></div>
            <div class="info-line" style="text-align: center; margin: 10mm 0;">
              There are no orders to display.
            </div>
          </div>
        `);
        orderListHTML.push(generateCustomerFooterHTML());
      }
    }

    // Return the HTML structure with updated CSS
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Order ${
    copyType === "rider" ? "Rider" : "Customer"
  } Copy - ${groupName}</title>
  <style>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  @page {
    size: 80mm auto;
    margin: 0;
    padding: 0;
  }
  
  html, body {
    direction: ltr !important;
    unicode-bidi: normal !important;
    text-align: left !important;
  }
  
  @media print {
    html, body {
      width: 80mm !important;
      min-width: 80mm !important;
      max-width: 80mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 0 !important;
      font-family: 'Arial Narrow', Arial, sans-serif !important;
      font-size: 10pt !important;
      line-height: 1.2 !important;
      background: white !important;
      color: black !important;
      direction: ltr !important;
      overflow: visible !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }
    
    .thermal-content {
      width: 76mm !important;
      min-width: 76mm !important;
      max-width: 76mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 1mm 2mm !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }

    .tiny-id-highlight {
  font-weight: bold;
  font-size: 10.5pt; /* Slightly larger than normal text */
  color: #000; /* Keep it black */
  margin-left: 0.5mm;
}
    
    .content-wrapper {
      width: 100% !important;
      display: block !important;
      margin: 0 auto !important;
    }
    
    .no-print { 
      display: none !important; 
    }
    
    /* Better page break control */
    .page-break {
      page-break-after: always;
    }
    
    .avoid-break {
      page-break-inside: avoid;
    }
  }
  
  body {
    font-family: 'Arial Narrow', Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.2;
    width: 80mm;
    min-width: 80mm;
    max-width: 80mm;
    height: auto;
    margin: 0 auto;
    padding: 0;
    background: white;
    color: black;
    overflow: visible;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .thermal-content {
    width: 76mm;
    min-width: 76mm;
    max-width: 76mm;
    height: auto;
    margin: 0 auto;
    padding: 1mm 2mm;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .content-wrapper {
    width: 100%;
    display: block;
    margin: 0 auto;
  }
  
  .header {
    text-align: center;
    margin: 0 0 2mm 0;
    padding: 0;
    width: 100%;
  }
  
  .logo-container {
    margin: 0 0 1mm 0;
    text-align: center;
    width: 100%;
  }
  
  .logo-image {
    max-width: 25mm;
    max-height: 12mm;
    height: auto;
    display: inline-block;
  }
  
  .header-title {
    font-weight: bold;
    font-size: 11pt;
    margin: 0 0 0.5mm 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
    width: 100%;
  }
  
  .header-subtitle {
    font-weight: bold;
    font-size: 9pt;
    margin: 0 0 1mm 0;
    text-transform: uppercase;
    text-align: center;
    width: 100%;
  }
  
  .info-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .info-line {
    font-size: 8pt;
    margin: 0.5mm 0;
    font-weight: normal;
    text-align: left;
    width: 100%;
  }
  
  .divider {
    border-top: 1px solid #000;
    margin: 2mm 0;
    height: 0;
    width: 100%;
  }
  
  .thin-divider {
    border-top: 0.5px solid #666;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .dotted-divider {
    border-top: 1px dotted #999;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .cutting-line {
    border-top: 2px dashed #000;
    margin: 3mm 0;
    height: 0;
    width: 100%;
  }
  
  .group-section {
    margin: 2mm 0;
    width: 100%;
  }
  
  .order-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .item-section {
    margin: 0.5mm 0;
    width: 100%;
  }
  
  .packing-section {
    margin: 2mm 0;
    padding: 1mm;
    border: 0.5px solid #dee2e6;
    width: 100%;
    overflow: hidden;
    word-wrap: break-word;
  }
  
  .order-spacing {
    height: 1mm;
    width: 100%;
  }
  
  .group-spacing {
    height: 2mm;
    width: 100%;
  }
  
  .group-header {
    font-weight: bold;
    font-size: 8pt;
    margin: 0.3mm 0;
    padding: 0;
    text-align: left;
    text-transform: uppercase;
    width: 100%;
  }
  
  .order-ref-with-bg {
    font-weight: bold;
    font-size: 9pt;
    margin: 0.5mm 0;
    padding: 1mm;
    text-align: left;
    text-transform: uppercase;
    background-color: black;
    color: #fff;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .packing-header {
    font-weight: bold;
    font-size: 9pt;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 1mm;
color:black;    width: 100%;
  }
  
  /* Add-on separator styles */
  .addon-separator {
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.2mm 0;
    font-size: 7pt;
    width: 100%;
    overflow: hidden;
  }
  
  .addon-header {
    font-weight: bold;
    font-style: italic;
color:black;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .addon-item {
    margin-left: 4mm !important;
  }
  
  .order-details {
    margin: 0.5mm 0 1mm 0;
    width: 100%;
  }
  
  .item-name {
    font-size: 9pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: left;
    width: 100%;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
  }
  
  .item-details {
    margin: 0.3mm 0 0 1mm;
    width: 100%;
  }
  
  .item-qty {
    font-size: 8pt;
    margin: 0.2mm 0;
    font-weight: bold;
    text-align: left;
    width: 100%;
  }
  
  .item-total {
    font-size: 8pt;
    margin: 0.2mm 0;
    font-weight: bold;
    text-align: right;
    width: 100%;
    color: #333;
  }
  
  .order-summary {
    margin: 1mm 0;
    padding: 1mm;
    background-color: #f5f5f5;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .summary-line {
    font-size: 8pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: right;
    width: 100%;
  }
  
  .total-amount {
    font-size: 9pt;
    font-weight: bolder;
    color: #000;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    margin-top: 0.5mm;
  }
  
  .paid-status {
    color: #28a745;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  .unpaid-status {
    color: #dc3545;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  /* Packing List Styles - FIXED FOR TEXT WRAPPING */
  .packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
    overflow: hidden;
  }
  
  .packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
    overflow: hidden;
  }
  
  .custom-packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
    overflow: hidden;
  }
  
  .custom-packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
    overflow: hidden;
  }
  
  .packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
    min-width: 0;
    overflow: hidden;
  }
  
  .custom-packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
    min-width: 0;
    overflow: hidden;
  }
  
  /* Dynamic font sizes for packing names based on length */
  .packing-name-normal {
    font-size: 9pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 52mm;
  }
  
  .packing-name-medium {
    font-size: 8.5pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 50mm;
  }
  
  .packing-name-long {
    font-size: 8pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 48mm;
  }
  
  .packing-name-very-long {
    font-size: 7.5pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 46mm;
  }
  
  .custom-packing-name-normal {
    font-size: 8pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 52mm;
  }
  
  .custom-packing-name-medium {
    font-size: 7.5pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 50mm;
  }
  
  .custom-packing-name-long {
    font-size: 7pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 48mm;
  }
  
  .custom-packing-name-very-long {
    font-size: 6.5pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 46mm;
  }
  
  .packing-qty {
    font-size: 9pt;
    font-weight: bold;
    text-align: right;
    min-width: 5mm;
    flex-shrink: 0;
    margin-left: 1mm;
  }
  
  .custom-packing-qty {
    font-size: 8pt;
    text-align: right;
    min-width: 5mm;
color:black;
    flex-shrink: 0;
    margin-left: 1mm;
  }
  
  .checkbox {
    width: 3mm;
    height: 3mm;
    border: 1px solid #000;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .custom-checkbox {
    width: 2.5mm;
    height: 2.5mm;
    border: 1px solid #666;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .footer {
    text-align: center;
    font-size: 8pt;
    margin: 3mm 0 0 0;
    padding: 1mm 0 0 0;
    border-top: 1px solid #000;
    font-weight: bold;
    width: 100%;
  }
  
  .footer-spacing {
    height: 5mm;
    margin: 2mm 0;
    width: 100%;
  }
  
  .page-break-spacing {
    height: 8mm;
    margin: 3mm 0;
    width: 100%;
  }

  .order-note-section {
    margin: 1mm 0;
    padding: 1mm;
    border: 0.5px dashed #888;
    width: 100%;
  }

  .order-note-label {
    font-size: 7.5pt;
    font-weight: bold;
    text-align: left;
    margin-bottom: 0.3mm;
    text-transform: uppercase;
  }

  .order-note-text {
    font-size: 7.5pt;
    font-weight: normal;
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
  }

  .payment-note-section {
    border-style: solid;
    border-color: #333;
  }
  
  /* Force left alignment for all text elements */
  .text-left {
    text-align: left !important;
  }
  
  .text-center {
    text-align: center !important;
  }
  
  .text-right {
    text-align: right !important;
  }

  @media print {
    .footer-spacing {
      height: 5mm !important;
      margin: 2mm 0 !important;
    }
    
    .page-break-spacing {
      height: 8mm !important;
      margin: 3mm 0 !important;
    }
    
    .cutting-line {
      border-top: 2px dashed #000 !important;
      margin: 3mm 0 !important;
    }
    
    .packing-name-normal,
    .packing-name-medium,
    .packing-name-long,
    .packing-name-very-long,
    .custom-packing-name-normal,
    .custom-packing-name-medium,
    .custom-packing-name-long,
    .custom-packing-name-very-long {
      max-width: 52mm !important;
      word-break: break-word !important;
    }
    
    .packing-item,
    .packing-item-no-checkbox,
    .custom-packing-item,
    .custom-packing-item-no-checkbox {
      page-break-inside: avoid !important;
    }
  }
</style>
</head>
<body>
  <div class="content-wrapper">
    <div class="thermal-content">
      ${orderListHTML.join("")}
    </div>
  </div>
</body>
</html>
    `;
  };

  // Print group orders by group
  const printGroupOrdersByGroup = async ({
    groupId,
    groupName,
    copyType,
  }: PrintByGroupOptions): Promise<void> => {
    setIsPrinting(true);
    try {
      // Find the specific group
      const group = groups?.find((g) => g.id === groupId);

      if (!group) {
        console.warn(`Group not found: ${groupId}`);
        return;
      }

      const htmlContent = generatePrintHTMLForGroup(group, groupName, copyType);

      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.style.opacity = "0";
      iframe.style.top = "-9999px";
      iframe.style.left = "-9999px";
      iframe.id = "thermal-print-frame";

      iframe.onload = () => {
        setTimeout(() => {
          try {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();

              const cleanup = () => {
                setTimeout(() => {
                  if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                  }
                  URL.revokeObjectURL(url);
                }, 100);
              };

              iframe.contentWindow.addEventListener("afterprint", cleanup);

              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                  URL.revokeObjectURL(url);
                }
              }, 5000);
            }
          } catch (error) {
            console.error("Print error:", error);
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            URL.revokeObjectURL(url);
          }
        }, 500);
      };

      iframe.src = url;
      document.body.appendChild(iframe);
    } catch (error) {
      console.error("Error printing:", error);
      throw error;
    } finally {
      setIsPrinting(false);
    }
  };

  // Generate HTML for specific cart groups
  const generatePrintHTMLForCart = (
    cartGroups: Group[],
    cartName: string,
    copyType: "rider" | "customer" = "rider"
  ): string => {
    const orderListHTML: string[] = [];

    const currentDate = new Date();
    const dateStr = currentDate.toLocaleDateString("en-GB");
    const timeStr = currentDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // Calculate total distance for all groups in this cart
    const totalDistance = cartGroups.reduce((total, group) => {
      return total + (group.deliveryRoute?.distanceInKm || 0);
    }, 0);

    const { riderName } = getCartInfo();

    // Get all orders from all groups in this cart
    const allCartOrders: PrintDeliveryOrder[] = [];
    cartGroups.forEach((group) => {
      const groupOrders = group.deliveryIds
        .map((deliveryId) => deliveries?.find((d) => d.id === deliveryId))
        .filter(isDefined);
      allCartOrders.push(...groupOrders);
    });

    if (copyType === "rider") {
      // RIDER COPY - Single document with all orders
      orderListHTML.push(`
        <div class="header">
          <div class="logo-container">
            <img src="${LOGO_URL}" alt="logo" class="logo-image" />
          </div>
          <div class="header-title">ORDER LIST</div>
          <div class="header-subtitle">RIDER COPY</div>
          ${cartName ? `<div class="header-subtitle">${cartName}</div>` : ""}
        </div>
        
        <div class="divider"></div>
        
        <div class="info-section">
          ${
            riderName
              ? `<div class="info-line">Rider Name : ${riderName}</div>`
              : ""
          }
          <div class="info-line">Date : ${dateStr}</div>
          <div class="info-line">Time : ${timeStr}</div>
          ${
            totalDistance > 0
              ? `<div class="info-line">Total Distance : ${formatDistance(
                  totalDistance
                )}</div>`
              : ""
          }
        </div>
      `);

      // Group orders by customer/organization for rider copy
      const customerOrdersMap = new Map<
        string,
        {
          entityId: string;
          entityName: string;
          entityType: "CUSTOMER" | "ORGANIZATION";
          entityLabel: string;
          orders: Array<{
            id: string;
            orderReference: string;
            orderItems: BaseOrderItem[];
            phoneNumber?: string;
            address?: string;
            paymentSummary?: any;
            totalPrice?: number;
            paymentCompleted?: boolean;
            paymentMethod?: string;
            originalOrder: PrintDeliveryOrder;
          }>;
        }
      >();

      // Collect orders without customer/organization info
      const ordersWithoutEntity: Array<{
        id: string;
        orderReference: string;
        orderItems: BaseOrderItem[];
        phoneNumber?: string;
        address?: string;
        paymentSummary?: any;
        totalPrice?: number;
        paymentCompleted?: boolean;
        paymentMethod?: string;
        originalOrder: PrintDeliveryOrder;
      }> = [];

      // Process all orders for this cart
      allCartOrders.forEach((order) => {
        const entityInfo = getEntityInfo(order);

        if (entityInfo) {
          const mapKey = `${entityInfo.type}_${entityInfo.id}`;

          if (!customerOrdersMap.has(mapKey)) {
            customerOrdersMap.set(mapKey, {
              entityId: entityInfo.id,
              entityName: entityInfo.name,
              entityType: entityInfo.type,
              entityLabel: entityInfo.label,
              orders: [],
            });
          }

          customerOrdersMap.get(mapKey)!.orders.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        } else {
          // Add to orders without customer/organization
          ordersWithoutEntity.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        }
      });

      // Display orders grouped by customer/organization
      if (customerOrdersMap.size > 0) {
        customerOrdersMap.forEach((entityData) => {

          entityData.orders.forEach((order, orderIndex) => {
            const orderType = "Group";

            orderListHTML.push(`
              <div class="order-section">
                <div class="order-ref-with-bg">ORDER REFERENCE : ${order.orderReference}</div>
                <div class="order-details">
                  <div class="info-line">ETA : </div>
                  <div class="info-line">Order Type : ${orderType}</div>
                  <div class="info-line">${entityData.entityLabel} : ${entityData.entityName}</div>
                  ${entityData.orders[0]?.phoneNumber ? `<div class="info-line">Contact Number : ${entityData.orders[0].phoneNumber}</div>` : ""}
                </div>
              </div>
            `);

            orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

            // Order items with checkboxes and Tiny IDs for rider copy
            order.orderItems.forEach((item, idx) => {
              // Format display name with Tiny ID if available
              const formattedItemName = formatDisplayName(
                item.displayName,
                item.tinyId
              );
              const itemNameClass = getItemDisplayNameClass(formattedItemName);
              const itemName = `${idx + 1}. ${formattedItemName}`;
              const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

              orderListHTML.push(`
                <div class="item-section">
                  <div class="item-name ${itemNameClass}">${itemName}</div>
                  <div class="item-details">
                    <div class="item-qty">QTY: ${item.quantity} x Rs.${
                item.unitPrice
              }</div>
                  </div>
                  <div class="item-total">Rs.${itemTotal}</div>
                  ${
                    idx < order.orderItems.length - 1
                      ? `<div class="dotted-divider"></div>`
                      : ""
                  }
                </div>
              `);
            });

            // Add order summary for rider copy with discount
            if (order.paymentSummary) {
              const paymentSummary = order.paymentSummary;
              const paymentStatusHTML = order.paymentCompleted
                ? `<div class="summary-line paid-status">PAID</div>`
                : "";

              const discountHTML =
                paymentSummary.discount && paymentSummary.discount > 0
                  ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                  : "";

              orderListHTML.push(`
                <div class="order-summary">
                  <div class="summary-line">Total Price: Rs.${
                    paymentSummary.totalPrice || order.totalPrice
                  }</div>
                  <div class="summary-line">Delivery Fee: Rs.${
                    paymentSummary.deliveryFee || 0
                  }</div>
                  ${discountHTML}
                  <div class="summary-line">Total Payable: Rs.${
                    paymentSummary.netAmount ||
                    paymentSummary.totalPrice +
                      (paymentSummary.deliveryFee || 0) -
                      (paymentSummary.discount || 0)
                  }</div>
                  <div class="summary-line">Payment Method: ${
                    order.paymentMethod || "N/A"
                  }</div>
                  ${paymentStatusHTML}
                </div>
              `);
            }

            // Add notes after payment breakdown
            orderListHTML.push(generateOrderNotesHTML(order.originalOrder, copyType));
            orderListHTML.push(`<div class="divider"></div>`);

            if (orderIndex < entityData.orders.length - 1) {
              orderListHTML.push(`<div class="order-spacing"></div>`);
            }
          });
        });
      }

      // Display orders without customer/organization info
      if (ordersWithoutEntity.length > 0) {
        orderListHTML.push(`
          <div class="group-section">
            <div class="group-header">OTHER ORDERS</div>
            <div class="thin-divider"></div>
            <div class="thin-divider"></div>
          </div>
        `);

        ordersWithoutEntity.forEach((order, orderIndex) => {
          const orderType = "Group";

          orderListHTML.push(`
            <div class="order-section">
              <div class="order-ref-with-bg">ORDER REF: ${order.orderReference}</div>
              <div class="order-details">
                <div class="info-line">ETA: </div>
                <div class="info-line">Order Type: ${orderType}</div>
              </div>
            </div>
          `);

          orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

          // Order items with checkboxes and Tiny IDs for rider copy
          order.orderItems.forEach((item, idx) => {
            // Format display name with Tiny ID if available
            const formattedItemName = formatDisplayName(
              item.displayName,
              item.tinyId
            );
            const itemNameClass = getItemDisplayNameClass(formattedItemName);
            const itemName = `${idx + 1}. ${formattedItemName}`;
            const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

            orderListHTML.push(`
              <div class="item-section">
                <div class="item-name ${itemNameClass}">${itemName}</div>
                <div class="item-details">
                  <div class="item-qty">QTY: ${item.quantity} x Rs.${
              item.unitPrice
            }</div>
                </div>
                <div class="item-total">Rs.${itemTotal}</div>
                ${
                  idx < order.orderItems.length - 1
                    ? `<div class="dotted-divider"></div>`
                    : ""
                }
              </div>
            `);
          });

          // Add order summary for rider copy with discount
          if (order.paymentSummary) {
            const paymentSummary = order.paymentSummary;
            const paymentStatusHTML = order.paymentCompleted
              ? `<div class="summary-line paid-status">PAID</div>`
              : "";

            const discountHTML =
              paymentSummary.discount && paymentSummary.discount > 0
                ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                : "";

            orderListHTML.push(`
              <div class="order-summary">
                <div class="summary-line">Total Price: Rs.${
                  paymentSummary.totalPrice || order.totalPrice
                }</div>
                <div class="summary-line">Delivery Fee: Rs.${
                  paymentSummary.deliveryFee || 0
                }</div>
                ${discountHTML}
                <div class="summary-line">Total Payable: Rs.${
                  paymentSummary.netAmount ||
                  paymentSummary.totalPrice +
                    (paymentSummary.deliveryFee || 0) -
                    (paymentSummary.discount || 0)
                }</div>
                <div class="summary-line">Payment Method: ${
                  order.paymentMethod || "N/A"
                }</div>
                ${paymentStatusHTML}
              </div>
            `);
          }

          // Add notes after payment breakdown
          orderListHTML.push(generateOrderNotesHTML(order.originalOrder, "rider"));
          orderListHTML.push(`<div class="divider"></div>`);

          if (orderIndex < ordersWithoutEntity.length - 1) {
            orderListHTML.push(`<div class="order-spacing"></div>`);
          }
        });
      }

      // Add consolidated packing list at the END for rider copy
      orderListHTML.push(
        generateConsolidatedPackingListHTML(allCartOrders, "rider")
      );
      orderListHTML.push(`<div class="divider"></div>`);

      // Add rider footer
      orderListHTML.push(`
        <div class="footer">
          <div>SAFE RIDE</div>
        </div>
      `);
    } else {
      // CUSTOMER COPY - Group orders by customer with all orders together
      const customerOrdersMap = new Map<
        string,
        {
          entityId: string;
          entityName: string;
          entityType: "CUSTOMER" | "ORGANIZATION";
          entityLabel: string;
          orders: Array<{
            id: string;
            orderReference: string;
            orderItems: BaseOrderItem[];
            phoneNumber?: string;
            address?: string;
            paymentSummary?: any;
            totalPrice?: number;
            paymentCompleted?: boolean;
            paymentMethod?: string;
            originalOrder: PrintDeliveryOrder;
          }>;
        }
      >();

      // Collect orders without customer/organization info
      const ordersWithoutEntity: Array<{
        id: string;
        orderReference: string;
        orderItems: BaseOrderItem[];
        phoneNumber?: string;
        address?: string;
        paymentSummary?: any;
        totalPrice?: number;
        paymentCompleted?: boolean;
        paymentMethod?: string;
        originalOrder: PrintDeliveryOrder;
      }> = [];

      // Process all orders for this cart
      allCartOrders.forEach((order) => {
        const entityInfo = getEntityInfo(order);

        if (entityInfo) {
          const mapKey = `${entityInfo.type}_${entityInfo.id}`;

          if (!customerOrdersMap.has(mapKey)) {
            customerOrdersMap.set(mapKey, {
              entityId: entityInfo.id,
              entityName: entityInfo.name,
              entityType: entityInfo.type,
              entityLabel: entityInfo.label,
              orders: [],
            });
          }

          customerOrdersMap.get(mapKey)!.orders.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        } else {
          // Add to orders without customer/organization
          ordersWithoutEntity.push({
            id: order.id,
            orderReference: order.orderReference,
            orderItems: order.orderItems,
            phoneNumber: order.phoneNumber,
            address: order.address || order.deliveryDetails?.address,
            paymentSummary: order.paymentSummary,
            totalPrice: order.totalPrice,
            paymentCompleted: order.paymentCompleted,
            paymentMethod: order.paymentMethod,
            originalOrder: order,
          });
        }
      });

      // Generate header HTML that will be repeated for each customer
      const generateCustomerHeaderHTML = (customerIndex: number) => `
        ${customerIndex > 0 ? `<div class="page-break-spacing"></div>` : ""}
        <div class="header">
          <div class="logo-container">
            <img src="${LOGO_URL}" alt="logo" class="logo-image" />
          </div>
          <div class="header-title">ORDER LIST</div>
          <div class="header-subtitle">CUSTOMER COPY</div>
        </div>
        
        <div class="divider"></div>
        
        <div class="info-section">
          <div class="info-line">Delivery Vehicle : ${cartName}</div>
          <div class="info-line">Date : ${dateStr}</div>
          <div class="info-line">Time : ${timeStr}</div>
        </div>
      `;

      // Generate footer HTML that will be repeated for each customer
      const generateCustomerFooterHTML = () => `
        <div class="footer">
          <div>Thank you</div>
          <div>55, 72 Vauxhall Ln, Colombo 00200</div>
          <div>HOTLINE: 011 444 1333</div>
        </div>
        <div class="cutting-line"></div>
        <div class="footer-spacing"></div>
      `;

      if (customerOrdersMap.size > 0) {
        let customerIndex = 0;
        customerOrdersMap.forEach((entityData) => {
          // Add header for each customer
          orderListHTML.push(generateCustomerHeaderHTML(customerIndex));

          entityData.orders.forEach((order, orderIndex) => {
            const orderType = "Group";

            orderListHTML.push(`
              <div class="order-section">
                <div class="order-ref-with-bg">ORDER REFERENCE : ${order.orderReference}</div>
                <div class="order-details">
                  <div class="info-line">ETA : </div>
                  <div class="info-line">Order Type : ${orderType}</div>
                  <div class="info-line">${entityData.entityLabel} : ${entityData.entityName}</div>
                </div>
              </div>
            `);

            orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

            // Order items with prices and Tiny IDs
            order.orderItems.forEach((item, idx) => {
              // Format display name with Tiny ID if available
              const formattedItemName = formatDisplayName(
                item.displayName,
                item.tinyId
              );
              const itemNameClass = getItemDisplayNameClass(formattedItemName);
              const itemName = `${idx + 1}. ${formattedItemName}`;
              const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

              orderListHTML.push(`
                <div class="item-section">
                  <div class="item-name ${itemNameClass}">${itemName}</div>
                  <div class="item-details">
                    <div class="item-qty">QTY: ${item.quantity} x Rs.${
                item.unitPrice
              }</div>
                  </div>
                  <div class="item-total">Rs.${itemTotal}</div>
                  ${
                    idx < order.orderItems.length - 1
                      ? `<div class="dotted-divider"></div>`
                      : ""
                  }
                </div>
              `);
            });

            // Add order summary for customer copy with discount
            if (order.paymentSummary) {
              const paymentSummary = order.paymentSummary;
              const paymentStatusHTML = order.paymentCompleted
                ? `<div class="summary-line paid-status">PAID</div>`
                : "";

              const discountHTML =
                paymentSummary.discount && paymentSummary.discount > 0
                  ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                  : "";

              orderListHTML.push(`
                <div class="order-summary">
                  <div class="summary-line">Total Price: Rs.${
                    paymentSummary.totalPrice || order.totalPrice
                  }</div>
                  <div class="summary-line">Delivery Fee: Rs.${
                    paymentSummary.deliveryFee || 0
                  }</div>
                  ${discountHTML}
                  <div class="summary-line">Total Payable: Rs.${
                    paymentSummary.netAmount ||
                    paymentSummary.totalPrice +
                      (paymentSummary.deliveryFee || 0) -
                      (paymentSummary.discount || 0)
                  }</div>
                  <div class="summary-line">Payment Method: ${
                    order.paymentMethod || "N/A"
                  }</div>
                  ${paymentStatusHTML}
                </div>
              `);
            }

            // Add notes after payment breakdown (skip for P&S customer copy)
            const isOrderPandS = order.originalOrder?.organization?.displayName?.includes('P&S') ?? false;
            if (!isOrderPandS) {
              orderListHTML.push(generateOrderNotesHTML(order.originalOrder, copyType));
            }
            orderListHTML.push(`<div class="divider"></div>`);

            if (orderIndex < entityData.orders.length - 1) {
              orderListHTML.push(`<div class="order-spacing"></div>`);
            }
          });

          // Add consolidated packing list WITHOUT checkboxes for customer copy
          const isEntityPandS = entityData.orders[0]?.originalOrder?.organization?.displayName?.includes('P&S') ?? false;
                    orderListHTML.push(
                      generateConsolidatedPackingListHTML(
                        entityData.orders.map((o) => o.originalOrder),
                        "customer",
                        isEntityPandS
                      )
                    );
          orderListHTML.push(`<div class="divider"></div>`);

          // Add footer for each customer
          orderListHTML.push(generateCustomerFooterHTML());

          // Add page break between customers (except for the last one)
          if (
            customerIndex < customerOrdersMap.size - 1 ||
            ordersWithoutEntity.length > 0
          ) {
            orderListHTML.push(`
              <div style="page-break-after: always;"></div>
            `);
          }
          customerIndex++;
        });
      }

      // Display orders without customer/organization info
      if (ordersWithoutEntity.length > 0) {
        // Add header for "OTHER ORDERS" section
        orderListHTML.push(generateCustomerHeaderHTML(customerOrdersMap.size));

        orderListHTML.push(`
          <div class="group-section">
            <div class="group-header">OTHER ORDERS</div>
            <div class="thin-divider"></div>
            <div class="thin-divider"></div>
          </div>
        `);

        ordersWithoutEntity.forEach((order, orderIndex) => {
          const orderType = "Group";

          orderListHTML.push(`
            <div class="order-section">
              <div class="order-ref-with-bg">ORDER REF: ${order.orderReference}</div>
              <div class="order-details">
                <div class="info-line">ETA: </div>
                <div class="info-line">Order Type: ${orderType}</div>
              </div>
            </div>
          `);

          orderListHTML.push(`
            <div class="items-section">
              <div class="thin-divider"></div>
              <div class="packing-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
            </div>
          `);

          // Order items with prices and Tiny IDs
          order.orderItems.forEach((item, idx) => {
            // Format display name with Tiny ID if available
            const formattedItemName = formatDisplayName(
              item.displayName,
              item.tinyId
            );
            const itemNameClass = getItemDisplayNameClass(formattedItemName);
            const itemName = `${idx + 1}. ${formattedItemName}`;
            const itemTotal = (item.quantity * item.unitPrice).toFixed(2);

            orderListHTML.push(`
              <div class="item-section">
                <div class="item-name ${itemNameClass}">${itemName}</div>
                <div class="item-details">
                  <div class="item-qty">QTY: ${item.quantity} x Rs.${
              item.unitPrice
            }</div>
                </div>
                <div class="item-total">Rs.${itemTotal}</div>
                ${
                  idx < order.orderItems.length - 1
                    ? `<div class="dotted-divider"></div>`
                    : ""
                }
              </div>
            `);
          });

          // Add order summary for customer copy with discount
          if (order.paymentSummary) {
            const paymentSummary = order.paymentSummary;
            const paymentStatusHTML = order.paymentCompleted
              ? `<div class="summary-line paid-status">PAID</div>`
              : "";

            const discountHTML =
              paymentSummary.discount && paymentSummary.discount > 0
                ? `<div class="summary-line">Discount: Rs.${paymentSummary.discount}</div>`
                : "";

            orderListHTML.push(`
              <div class="order-summary">
                <div class="summary-line">Total Price: Rs.${
                  paymentSummary.totalPrice || order.totalPrice
                }</div>
                <div class="summary-line">Delivery Fee: Rs.${
                  paymentSummary.deliveryFee || 0
                }</div>
                ${discountHTML}
                <div class="summary-line">Total Payable: Rs.${
                  paymentSummary.netAmount ||
                  paymentSummary.totalPrice +
                    (paymentSummary.deliveryFee || 0) -
                    (paymentSummary.discount || 0)
                }</div>
                <div class="summary-line">Payment Method: ${
                  order.paymentMethod || "N/A"
                }</div>
                ${paymentStatusHTML}
              </div>
            `);
          }

          // Add notes after payment breakdown
          orderListHTML.push(generateOrderNotesHTML(order.originalOrder, "customer"));
          orderListHTML.push(`<div class="divider"></div>`);

          if (orderIndex < ordersWithoutEntity.length - 1) {
            orderListHTML.push(`<div class="order-spacing"></div>`);
          }
        });

        // Add consolidated packing list for "OTHER ORDERS" section
        orderListHTML.push(
          generateConsolidatedPackingListHTML(
            ordersWithoutEntity.map((o) => o.originalOrder),
            "customer"
          )
        );
        orderListHTML.push(`<div class="divider"></div>`);

        // Add footer for "OTHER ORDERS" section
        orderListHTML.push(generateCustomerFooterHTML());
      }

      if (customerOrdersMap.size === 0 && ordersWithoutEntity.length === 0) {
        orderListHTML.push(generateCustomerHeaderHTML(0));
        orderListHTML.push(`
          <div class="group-section">
            <div class="group-header">NO ORDERS AVAILABLE</div>
            <div class="thin-divider"></div>
            <div class="info-line" style="text-align: center; margin: 10mm 0;">
              There are no orders to display.
            </div>
          </div>
        `);
        orderListHTML.push(generateCustomerFooterHTML());
      }
    }

    // Return the HTML structure
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Order ${copyType === "rider" ? "Rider" : "Customer"} Copy</title>
  <style>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  @page {
    size: 80mm auto;
    margin: 0;
    padding: 0;
  }
  
  html, body {
    direction: ltr !important;
    unicode-bidi: normal !important;
    text-align: left !important;
  }
  
  @media print {
    html, body {
      width: 80mm !important;
      min-width: 80mm !important;
      max-width: 80mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 0 !important;
      font-family: 'Arial Narrow', Arial, sans-serif !important;
      font-size: 10pt !important;
      line-height: 1.2 !important;
      background: white !important;
      color: black !important;
      direction: ltr !important;
      overflow: visible !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }
    
    .thermal-content {
      width: 76mm !important;
      min-width: 76mm !important;
      max-width: 76mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 1mm 2mm !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }
    
    .content-wrapper {
      width: 100% !important;
      display: block !important;
      margin: 0 auto !important;
    }
    
    .no-print { 
      display: none !important; 
    }
    
    /* Better page break control */
    .page-break {
      page-break-after: always;
    }
    
    .avoid-break {
      page-break-inside: avoid;
    }
  }
  
  body {
    font-family: 'Arial Narrow', Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.2;
    width: 80mm;
    min-width: 80mm;
    max-width: 80mm;
    height: auto;
    margin: 0 auto;
    padding: 0;
    background: white;
    color: black;
    overflow: visible;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .thermal-content {
    width: 76mm;
    min-width: 76mm;
    max-width: 76mm;
    height: auto;
    margin: 0 auto;
    padding: 1mm 2mm;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .content-wrapper {
    width: 100%;
    display: block;
    margin: 0 auto;
  }
  
  .header {
    text-align: center;
    margin: 0 0 2mm 0;
    padding: 0;
    width: 100%;
  }
  
  .logo-container {
    margin: 0 0 1mm 0;
    text-align: center;
    width: 100%;
  }
  
  .logo-image {
    max-width: 25mm;
    max-height: 12mm;
    height: auto;
    display: inline-block;
  }
  
  .header-title {
    font-weight: bold;
    font-size: 11pt;
    margin: 0 0 0.5mm 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
    width: 100%;
  }
  
  .header-subtitle {
    font-weight: bold;
    font-size: 9pt;
    margin: 0 0 1mm 0;
    text-transform: uppercase;
    text-align: center;
    width: 100%;
  }
  
  .info-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .info-line {
    font-size: 8pt;
    margin: 0.5mm 0;
    font-weight: normal;
    text-align: left;
    width: 100%;
  }
  
  .divider {
    border-top: 1px solid #000;
    margin: 2mm 0;
    height: 0;
    width: 100%;
  }
  
  .thin-divider {
    border-top: 0.5px solid #666;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .dotted-divider {
    border-top: 1px dotted #999;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .cutting-line {
    border-top: 2px dashed #000;
    margin: 3mm 0;
    height: 0;
    width: 100%;
  }
  
  .group-section {
    margin: 2mm 0;
    width: 100%;
  }
  
  .order-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .item-section {
    margin: 0.5mm 0;
    width: 100%;
  }
  
  .packing-section {
    margin: 2mm 0;
    padding: 1mm;
    border: 0.5px solid #dee2e6;
    width: 100%;
    overflow: hidden;
    word-wrap: break-word;
  }
  
    .tiny-id-highlight {
    font-weight: 700 !important;
    font-size: 10pt !important;
    padding: 0.2mm 0.5mm !important;
    border-radius: 1mm !important;
    display: inline-block !important;
    margin-left: 0.5mm !important;
  }

  .order-spacing {
    height: 1mm;
    width: 100%;
  }
  
  .group-spacing {
    height: 2mm;
    width: 100%;
  }
  
  .group-header {
    font-weight: bold;
    font-size: 8pt;
    margin: 0.3mm 0;
    padding: 0;
    text-align: left;
    text-transform: uppercase;
    width: 100%;
  }
  
  .order-ref-with-bg {
    font-weight: bold;
    font-size: 9pt;
    margin: 0.5mm 0;
    padding: 1mm;
    text-align: left;
    text-transform: uppercase;
    background-color: black;
    color: #fff;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .packing-header {
    font-weight: bold;
    font-size: 9pt;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 1mm;
color:black;    width: 100%;
  }
  
  /* Add-on separator styles */
  .addon-separator {
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.2mm 0;
    font-size: 7pt;
    width: 100%;
    overflow: hidden;
  }
  
  .addon-header {
    font-weight: bold;
    font-style: italic;
color:black;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .addon-item {
    margin-left: 4mm !important;
  }
  
  .order-details {
    margin: 0.5mm 0 1mm 0;
    width: 100%;
  }
  
  .item-name {
    font-size: 9pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: left;
    width: 100%;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
  }
  
  .item-details {
    margin: 0.3mm 0 0 1mm;
    width: 100%;
  }
  
  .item-qty {
    font-size: 8pt;
    margin: 0.2mm 0;
    font-weight: bold;
    text-align: left;
    width: 100%;
  }
  
  .item-total {
    font-size: 8pt;
    margin: 0.2mm 0;
    font-weight: bold;
    text-align: right;
    width: 100%;
    color: #333;
  }
  
  .order-summary {
    margin: 1mm 0;
    padding: 1mm;
    background-color: #f5f5f5;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .summary-line {
    font-size: 8pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: right;
    width: 100%;
  }
  
  .total-amount {
    font-size: 9pt;
    font-weight: bolder;
    color: #000;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    margin-top: 0.5mm;
  }
  
  .paid-status {
    color: #28a745;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  .unpaid-status {
    color: #dc3545;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  /* Packing List Styles - FIXED FOR TEXT WRAPPING */
  .packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
    overflow: hidden;
  }
  
  .packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
    overflow: hidden;
  }
  
  .custom-packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
    overflow: hidden;
  }
  
  .custom-packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
    overflow: hidden;
  }
  
  .packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
    min-width: 0;
    overflow: hidden;
  }
  
  .custom-packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
    min-width: 0;
    overflow: hidden;
  }
  
  /* Dynamic font sizes for packing names based on length */
  .packing-name-normal {
    font-size: 9pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 52mm;
  }
  
  .packing-name-medium {
    font-size: 8.5pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 50mm;
  }
  
  .packing-name-long {
    font-size: 8pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 48mm;
  }
  
  .packing-name-very-long {
    font-size: 7.5pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 46mm;
  }
  
  .custom-packing-name-normal {
    font-size: 8pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 52mm;
  }
  
  .custom-packing-name-medium {
    font-size: 7.5pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 50mm;
  }
  
  .custom-packing-name-long {
    font-size: 7pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 48mm;
  }
  
  .custom-packing-name-very-long {
    font-size: 6.5pt;
    text-align: left;
    flex: 1;
color:black;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 46mm;
  }
  
  .packing-qty {
    font-size: 9pt;
    font-weight: bold;
    text-align: right;
    min-width: 5mm;
    flex-shrink: 0;
    margin-left: 1mm;
  }
  
  .custom-packing-qty {
    font-size: 8pt;
    text-align: right;
    min-width: 5mm;
    color: black;
    flex-shrink: 0;
    margin-left: 1mm;
  }
  
  .checkbox {
    width: 3mm;
    height: 3mm;
    border: 1px solid #000;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .custom-checkbox {
    width: 2.5mm;
    height: 2.5mm;
    border: 1px solid #666;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .footer {
    text-align: center;
    font-size: 8pt;
    margin: 3mm 0 0 0;
    padding: 1mm 0 0 0;
    border-top: 1px solid #000;
    font-weight: bold;
    width: 100%;
  }
  
  .footer-spacing {
    height: 5mm;
    margin: 2mm 0;
    width: 100%;
  }
  
  .page-break-spacing {
    height: 8mm;
    margin: 3mm 0;
    width: 100%;
  }

  .order-note-section {
    margin: 1mm 0;
    padding: 1mm;
    border: 0.5px dashed #888;
    width: 100%;
  }

  .order-note-label {
    font-size: 7.5pt;
    font-weight: bold;
    text-align: left;
    margin-bottom: 0.3mm;
    text-transform: uppercase;
  }

  .order-note-text {
    font-size: 7.5pt;
    font-weight: normal;
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
  }

  .payment-note-section {
    border-style: solid;
    border-color: #333;
  }
  
  /* Force left alignment for all text elements */
  .text-left {
    text-align: left !important;
  }
  
  .text-center {
    text-align: center !important;
  }
  
  .text-right {
    text-align: right !important;
  }

  @media print {
    .footer-spacing {
      height: 5mm !important;
      margin: 2mm 0 !important;
    }
    
    .page-break-spacing {
      height: 8mm !important;
      margin: 3mm 0 !important;
    }
    
    .cutting-line {
      border-top: 2px dashed #000 !important;
      margin: 3mm 0 !important;
    }
    
    .packing-name-normal,
    .packing-name-medium,
    .packing-name-long,
    .packing-name-very-long,
    .custom-packing-name-normal,
    .custom-packing-name-medium,
    .custom-packing-name-long,
    .custom-packing-name-very-long {
      max-width: 52mm !important;
      word-break: break-word !important;
    }
    
    .packing-item,
    .packing-item-no-checkbox,
    .custom-packing-item,
    .custom-packing-item-no-checkbox {
      page-break-inside: avoid !important;
    }
  }
</style>
</head>
<body>
  <div class="content-wrapper">
    <div class="thermal-content">
      ${orderListHTML.join("")}
    </div>
  </div>
</body>
</html>
    `;
  };

  // Print group orders by cart
const printGroupOrdersByCart = async ({
  cartId,
  cartName,
  copyType,
}: PrintByCartOptions): Promise<void> => {
  setIsPrinting(true);
  try {
    // Filter groups to only include those for the specific cart
    // Check both cartId and deliveryCartId
    const filteredGroups =
      groups?.filter((group) => {
        if (!group.cart) return false;
        
        // Check if this is the delivery cart ID
        const deliveryCartId = (group.cart as any)?.deliveryCartId;
        
        // Check both deliveryCartId and cartId
        return deliveryCartId === cartId || group.cart.cartId === cartId;
      }) || [];

    // console.log(`Printing cart: ${cartId} (${cartName})`);
    // console.log(`Found ${filteredGroups.length} groups for this cart`);
    // console.log('Groups found:', filteredGroups.map(g => ({
    //   id: g.id,
    //   name: g.name,
    //   cart: g.cart,
    //   deliveryCartId: (g.cart as any)?.deliveryCartId,
    //   deliveryIds: g.deliveryIds?.length || 0,
    //   isCollectionGrouped: g.isCollectionGrouped
    // })));

    if (filteredGroups.length === 0) {
      console.warn(`No groups found for cart: ${cartId}`);
      alert(`No groups found for cart: ${cartName}`);
      return;
    }

    // Also check if cart has collection groups (finalized groups)
    const hasCollectionGroups = filteredGroups.some(g => g.isCollectionGrouped);
    if (!hasCollectionGroups) {
      console.warn(`Cart ${cartName} has no finalized groups to print`);
      alert(`Cart ${cartName} has no finalized groups to print`);
      return;
    }

    const htmlContent = generatePrintHTMLForCart(
      filteredGroups,
      cartName,
      copyType
    );

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.style.opacity = "0";
    iframe.style.top = "-9999px";
    iframe.style.left = "-9999px";
    iframe.id = "thermal-print-frame";
    iframe.name = "print-frame";

    iframe.onload = () => {
      setTimeout(() => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            const cleanup = () => {
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
                URL.revokeObjectURL(url);
              }, 100);
            };

            iframe.contentWindow.addEventListener("afterprint", cleanup);

            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(url);
              }
            }, 5000);
          }
        } catch (error) {
          console.error("Print error:", error);
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          URL.revokeObjectURL(url);
        }
      }, 500);
    };

    iframe.src = url;
    document.body.appendChild(iframe);
  } catch (error) {
    console.error("Error printing:", error);
    alert(`Failed to print ${copyType} copy for cart ${cartName}: ${error}`);
    throw error;
  } finally {
    setIsPrinting(false);
  }
};
  const showPreview = (copyType: "rider" | "customer" = "rider"): void => {
    setPreviewVisible(true);
  };

  const hidePreview = (): void => {
    setPreviewVisible(false);
    setPreviewHTML("");
  };

  const availableOrders = getAllAvailableOrders();

  return {
    // Group printing functions
    printGroupOrdersByGroup,
    printGroupOrdersByCart,

    // Order-wise printing functions
    printOrder,
    printMultipleOrders,

    // State and utility functions
    isPrinting,
    previewVisible,
    previewHTML,
    showPreview,
    hidePreview,

    // Data availability indicators
    hasOrders: availableOrders.length > 0,
    availableOrders,
  };
};

export default useGroupOrderPrint;
